import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/authStore";
import { useChat } from "../../chat/useChat";
import { chatApi } from "../../api/chatApi";
import { psychologistVerificationApi } from "../../api/psychologistVerificationApi";
import { useToast } from "../../ui/toast/ToastProvider";
import {
  buildPsychologistProfilePath,
  isExternalHttpUrl,
  resolvePsychologistProfileRoute,
} from "../../utils/psychologistProfile";
import Icon from "./components/Icon";
import ChatScrollFab from "./components/ChatScrollFab";
import ChatMediaGallery from "./ChatMediaGallery";
import ChatMessageSearchBar from "./ChatMessageSearchBar";
import ReactionPicker from "./ReactionPicker";
import ChatRecommendationPicker from "./ChatRecommendationPicker";
import ChatConfirmDelete from "./ChatConfirmDelete";
import { useChatUIState } from "./hooks/useChatUIState";
import {
  FILTER_ALL,
  FILTER_CLIENTS,
  FILTER_GROUPS,
  FILTER_ARCHIVE,
  CHAT_MAX_MESSAGE_LENGTH,
  CHAT_MAX_FILES_PER_MESSAGE,
  CHAT_MAX_FILE_SIZE_BYTES,
} from "./utils/constants";
import {
  toTimestamp,
  formatDialogTime,
  formatMessageTime,
  formatDateSeparator,
  formatDateKey,
  initials,
  buildTypingText,
  bytesLabel,
} from "./utils/formatters";
import { getDialogTitle, getDialogPreview, isGroupDialog, isArchivedDialog } from "./utils/dialogUtils";
import {
  isMineMessage,
  isMessageDeleted,
  isMessageEdited,
  isReadByMe,
  isReadByOther,
  messageDomKey,
  isServerMessageId,
} from "./utils/messageUtils";
import { fileFingerprint, isBlockedFile, safeExternalUrl } from "./utils/fileUtils";
import { safeDomId } from "./utils/domUtils";
import { EMOJI_CATEGORIES, STICKER_CATEGORIES } from "./utils/pickerData";
import { buildChatContentCardPayload, buildChatStickerPayload } from "../../chat/contentPayloads";
import { triggerReactionEffectFromElement } from "../../chat/reactionFx";

import {
  CHAT_ADMIN_ROLE_CODES,
  CHAT_HOST_ROLE_CODES,
  CHAT_PSYCHOLOGIST_ROLE_CODES,
  CheckIcon,
  extractMessageReaders,
  formatReaderReadAt,
  normalizeRoleCode,
  normalizeSearchUser,
  toUserId,
} from "./utils/chatUiModel";
const CHAT_UI_INFO_PANEL_KEY = "chat_ui_info_panel_open_v1";
const CHAT_UI_DIALOG_SIDEBAR_KEY = "chat_ui_dialog_sidebar_hidden_v1";

const CHAT_WRITE_MODE_ALL = "all";
const CHAT_WRITE_MODE_HOST_ONLY = "host_only";
const CHAT_WRITE_MODE_SELECTED = "selected";
const CHAT_MEDIA_MODE_ALL = "all";
const CHAT_MEDIA_MODE_HOST_ONLY = "host_only";
const CHAT_MEDIA_MODE_DISABLED = "disabled";
const CHAT_IMAGE_EXT_RE = /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)(?:$|[?#])/i;
const AEGIS_AVATAR_URL = `${process.env.PUBLIC_URL || ""}/icons/aegis-avatar.webp`;
const ACTION_FEED_META_PREFIX = "%%BUROSOUL_ACTION_V1%%";
const CHAT_VERIFICATION_STATUS_CACHE_KEY = "bs:chat:verification-status:v2";

function isAegisLabel(value) {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return false;
  return (
    text.includes("aegis") ||
    text.includes("action-feed") ||
    text.includes("action feed") ||
    text.includes("системные сообщения администратору")
  );
}

function isAegisDialogCandidate(dialog, titleOverride = "") {
  if (!dialog) return false;
  return [
    titleOverride,
    dialog?.title,
    dialog?.name,
    dialog?.displayName,
    dialog?.lastMessageSender,
    dialog?.category,
    dialog?.systemType,
  ].some(isAegisLabel);
}

function isAdminNotificationsDialog(dialog, titleOverride = "") {
  if (!dialog) return false;
  const blob = [
    titleOverride,
    dialog?.title,
    dialog?.name,
    dialog?.displayName,
    dialog?.category,
    dialog?.systemType,
    dialog?.system_type,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean)
    .join(" ");

  if (!blob) return false;
  if (blob.includes("системные сообщения администратору")) return true;
  if (blob.includes("уведомления админам")) return true;
  if (blob.includes("уведомления администраторам")) return true;
  if (blob.includes("admin notifications")) return true;
  if (blob.includes("admin notification feed")) return true;
  return false;
}

function stripActionFeedMetaLine(textValue) {
  const text = String(textValue || "");
  if (!text.includes(ACTION_FEED_META_PREFIX)) return text;
  return text
    .split(/\r?\n/)
    .filter((line) => !String(line || "").trim().startsWith(ACTION_FEED_META_PREFIX))
    .join("\n")
    .trim();
}

function normalizeChatTextForDisplay(textValue) {
  const stripped = stripActionFeedMetaLine(textValue);
  if (!stripped) return "";
  let normalized = stripped.replace(/\u00A0/g, " ").replace(/&nbsp;/gi, " ");
  if (typeof window !== "undefined" && normalized.includes("&")) {
    try {
      const textarea = window.document.createElement("textarea");
      textarea.innerHTML = normalized;
      normalized = String(textarea.value || normalized);
    } catch {
      // ignore decode failures and keep original text
    }
  }
  return normalized;
}

function parseActionFeedMeta(textValue) {
  const text = String(textValue || "");
  if (!text) return null;
  const line = text
    .split(/\r?\n/)
    .map((item) => String(item || "").trim())
    .find((item) => item.startsWith(ACTION_FEED_META_PREFIX));
  if (!line) return null;
  const payload = line.slice(ACTION_FEED_META_PREFIX.length).trim();
  if (!payload) return null;
  try {
    const parsed = JSON.parse(payload);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeVerificationResolution(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return "";
  if (raw === "APPROVED" || raw === "APPROVE" || raw === "VERIFIED" || raw === "VERIFY") return "approve";
  if (raw === "REJECTED" || raw === "REJECT" || raw === "DECLINED" || raw === "DENIED") return "reject";
  if (raw === "ОДОБРЕНО") return "approve";
  if (raw === "ОТКЛОНЕНО" || raw === "ОТКЛОНЕН") return "reject";
  return "";
}

function isVerificationResolvedStatus(value) {
  return Boolean(normalizeVerificationResolution(value));
}

function extractVerificationResolutionFromText(rawText) {
  const text = String(rawText || "");
  const statusLine = text.match(/Статус\s*:\s*([^\n\r]+)/i);
  const fromStatusLine = normalizeVerificationResolution(statusLine?.[1] || "");
  if (fromStatusLine) return fromStatusLine;
  if (/верификац\w*\s+одобрен/i.test(text)) return "approve";
  if (/верификац\w*\s+отклон/i.test(text)) return "reject";
  return "";
}

function pickVerificationItem(payload) {
  if (payload && typeof payload === "object") {
    if (payload.item && typeof payload.item === "object") return payload.item;
    if (payload.data && typeof payload.data === "object") {
      if (payload.data.item && typeof payload.data.item === "object") return payload.data.item;
      return payload.data;
    }
    return payload;
  }
  return null;
}

function verificationResolutionLabel(resolution) {
  if (resolution === "approve") return "Одобрено";
  if (resolution === "reject") return "Отклонено";
  return "";
}

function applyVerificationResolutionToText(textValue, resolution, reviewerName = "") {
  const text = normalizeChatTextForDisplay(textValue);
  const statusLabel = verificationResolutionLabel(String(resolution || "").trim().toLowerCase());
  if (!statusLabel) return text;

  let nextText = text;
  if (/Статус\s*:/i.test(nextText)) {
    nextText = nextText.replace(/Статус\s*:\s*[^\n\r]+/i, `Статус: ${statusLabel}`);
  } else {
    nextText = `${nextText}\nСтатус: ${statusLabel}`.trim();
  }

  const reviewer = String(reviewerName || "").trim();
  if (!reviewer) return nextText;
  if (/Проверил\s*:/i.test(nextText)) {
    return nextText.replace(/Проверил\s*:\s*[^\n\r]+/i, `Проверил: ${reviewer}`);
  }
  return `${nextText}\nПроверил: ${reviewer}`.trim();
}

function readVerificationStatusCache() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.sessionStorage.getItem(CHAT_VERIFICATION_STATUS_CACHE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};

    const out = {};
    Object.entries(parsed).forEach(([userId, value]) => {
      const normalizedUserId = String(toUserId(userId) || "").trim();
      if (!normalizedUserId || !value || typeof value !== "object") return;
      const status = String(value.status || "").trim().toUpperCase();
      if (!isVerificationResolvedStatus(status)) return;
      const reviewerName = String(value.reviewerName || "").trim();
      const reviewerUserId = toUserId(value.reviewerUserId);
      const fetchedAt = Number(value.fetchedAt || 0);
      if (!status) return;
      out[normalizedUserId] = {
        status,
        reviewerName,
        reviewerUserId,
        fetchedAt: Number.isFinite(fetchedAt) ? fetchedAt : 0,
      };
    });
    return out;
  } catch {
    return {};
  }
}

function writeVerificationStatusCache(nextValue) {
  if (typeof window === "undefined") return;
  try {
    const source = nextValue && typeof nextValue === "object" ? nextValue : {};
    const safe = Object.fromEntries(
      Object.entries(source).filter(([, value]) =>
        isVerificationResolvedStatus(String(value?.status || "").trim().toUpperCase())
      )
    );
    window.sessionStorage.setItem(CHAT_VERIFICATION_STATUS_CACHE_KEY, JSON.stringify(safe));
  } catch {
    // ignore storage errors
  }
}

function mergeVerificationStatusCacheEntry(prev, userId, patch) {
  const key = String(toUserId(userId) || "").trim();
  if (!key) return prev;

  const current = prev && typeof prev === "object" ? prev : {};
  const status = String(patch?.status || "").trim().toUpperCase();
  if (!status) {
    const next = { ...current };
    delete next[key];
    return next;
  }

  return {
    ...current,
    [key]: {
      ...(current[key] || {}),
      ...patch,
      status,
      reviewerName: String(patch?.reviewerName || "").trim(),
      reviewerUserId: toUserId(patch?.reviewerUserId),
      fetchedAt: Number.isFinite(Number(patch?.fetchedAt))
        ? Number(patch.fetchedAt)
        : Number(current[key]?.fetchedAt || 0),
    },
  };
}

function extractPsychologistVerificationAction(message) {
  if (!message) return null;
  const raw = String(message?.rawContent || message?.text || message?.content || "");
  if (!raw) return null;

  const meta = parseActionFeedMeta(raw);
  const resolvedFromMeta = normalizeVerificationResolution(
    meta?.resolved ?? meta?.decision ?? meta?.result ?? meta?.status ?? meta?.verificationStatus
  );
  const resolvedFromText = extractVerificationResolutionFromText(raw);

  if (String(meta?.type || "").trim().toUpperCase() === "PSYCHOLOGIST_VERIFICATION_REQUESTED") {
    const uid = toUserId(meta?.userId ?? meta?.user_id ?? meta?.targetUserId ?? meta?.target_user_id);
    if (uid) return { userId: uid, resolved: resolvedFromMeta || resolvedFromText || "" };
  }

  if (!/заявка\s+на\s+верификацию\s+психолога/i.test(raw)) return null;
  const idMatchers = [/\(ID\s*(\d+)\)/i, /\bid\s*[:=]\s*(\d+)/i, /User ID\s*[:=]\s*(\d+)/i];
  for (const pattern of idMatchers) {
    const matched = raw.match(pattern);
    if (matched && matched[1]) {
      const uid = toUserId(matched[1]);
      if (uid) return { userId: uid, resolved: resolvedFromMeta || resolvedFromText || "" };
    }
  }
  return null;
}

function cleanAttachmentPath(value) {
  return String(value || "").trim().split("#")[0].split("?")[0];
}

function isImageLikeFileValue(value) {
  const kind = String(value?.kind || "").trim().toLowerCase();
  if (kind === "image") return true;
  const mime = String(value?.mime || value?.contentType || value?.type || "").trim().toLowerCase();
  if (mime.startsWith("image/")) return true;
  const name = cleanAttachmentPath(value?.name || value?.filename || value?.originalName || value?.original_name || "");
  const url = cleanAttachmentPath(value?.url || value?.fileUrl || value?.downloadUrl || "");
  return CHAT_IMAGE_EXT_RE.test(name) || CHAT_IMAGE_EXT_RE.test(url);
}

function normalizeMode(value, fallback, allowed) {
  const raw = String(value || "").trim().toLowerCase().replace(/[-\s]+/g, "_");
  if (!raw) return fallback;
  if (raw === "host" || raw === "hosts" || raw === "hostonly" || raw === "owner_only" || raw === "admin_only") {
    return allowed.includes(CHAT_WRITE_MODE_HOST_ONLY) || allowed.includes(CHAT_MEDIA_MODE_HOST_ONLY)
      ? CHAT_WRITE_MODE_HOST_ONLY
      : fallback;
  }
  if (raw === "members" || raw === "everyone" || raw === "enabled" || raw === "allow" || raw === "allowed") {
    return allowed.includes(CHAT_WRITE_MODE_ALL) || allowed.includes(CHAT_MEDIA_MODE_ALL)
      ? CHAT_WRITE_MODE_ALL
      : fallback;
  }
  if (raw === "none" || raw === "disabled" || raw === "deny" || raw === "blocked") {
    return allowed.includes(CHAT_MEDIA_MODE_DISABLED) ? CHAT_MEDIA_MODE_DISABLED : fallback;
  }
  if (allowed.includes(raw)) return raw;
  return fallback;
}

function collectAllowedUserIds(value) {
  const raw = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];
  return raw
    .map((item) => {
      if (item && typeof item === "object") return toUserId(item.userId ?? item.id);
      return toUserId(item);
    })
    .filter(Boolean)
    .map(String)
    .filter((item, index, arr) => arr.indexOf(item) === index);
}

function extractDialogSettings(dialog, localOverride = {}) {
  const source = {
    ...(dialog?.permissions && typeof dialog.permissions === "object" ? dialog.permissions : {}),
    ...(dialog?.settings && typeof dialog.settings === "object" ? dialog.settings : {}),
    ...(dialog?.chatSettings && typeof dialog.chatSettings === "object" ? dialog.chatSettings : {}),
    ...localOverride,
  };

  const writeMode = normalizeMode(
    source.writeMode ??
      source.messageWriteMode ??
      source.messageMode ??
      source.sendMode ??
      source.canWriteMode ??
      dialog?.writeMode ??
      dialog?.messageWriteMode ??
      dialog?.sendMode,
    CHAT_WRITE_MODE_ALL,
    [CHAT_WRITE_MODE_ALL, CHAT_WRITE_MODE_HOST_ONLY, CHAT_WRITE_MODE_SELECTED]
  );

  const mediaMode = normalizeMode(
    source.mediaMode ??
      source.attachmentMode ??
      source.fileMode ??
      source.sendMediaMode ??
      dialog?.mediaMode ??
      dialog?.attachmentMode ??
      (source.mediaEnabled === false || dialog?.mediaEnabled === false ? CHAT_MEDIA_MODE_DISABLED : ""),
    CHAT_MEDIA_MODE_ALL,
    [CHAT_MEDIA_MODE_ALL, CHAT_MEDIA_MODE_HOST_ONLY, CHAT_MEDIA_MODE_DISABLED]
  );

  const allowedUserIds = collectAllowedUserIds(
    source.allowedUserIds ??
      source.writeAllowedUserIds ??
      source.allowedWriters ??
      source.writerUserIds ??
      dialog?.allowedUserIds ??
      dialog?.writeAllowedUserIds
  );

  return { writeMode, mediaMode, allowedUserIds };
}

function messagePlainText(message) {
  const parts = [
    message?.text,
    message?.content,
    message?.contentCard?.title,
    message?.contentCard?.subtitle,
    message?.contentCard?.description,
    message?.sticker?.emoji,
  ];
  if (Array.isArray(message?.attachments)) {
    message.attachments.forEach((file) => parts.push(file?.name, file?.mime, file?.kind));
  }
  return parts.filter(Boolean).join(" ");
}

function buildMessagePreview(message) {
  const text = String(message?.text || message?.contentCard?.title || message?.sticker?.emoji || "").trim();
  if (text) return text.length > 140 ? `${text.slice(0, 139)}…` : text;
  const firstFile = Array.isArray(message?.attachments) ? message.attachments[0] : null;
  return firstFile?.name || "Вложение";
}

function formatRestrictionDateTime(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getSystemChatNotice(message, dialogTitle = "") {
  if (!message) return "";
  const type = String(
    message.messageType ||
      message.eventType ||
      message.event_type ||
      message.systemType ||
      message.system_type ||
      message.type ||
      ""
  )
    .trim()
    .toLowerCase();
  const rawText = String(message.text || message.content || message.rawContent || message.body || "").trim();
  const explicitTitle = String(
    message.newTitle ||
      message.new_title ||
      message.chatTitle ||
      message.chat_title ||
      message.title ||
      ""
  ).trim();
  const senderTitle = String(message.fromName || message.fromUserName || "").trim();
  const title = explicitTitle || senderTitle;
  const hasAttachments = Array.isArray(message.attachments) && message.attachments.length > 0;
  const fromId = Number(message.fromUserId ?? message.senderUserId ?? message.senderId ?? message.userId);
  const hasValidSender = Number.isFinite(fromId) && fromId > 0;
  const deleted = isMessageDeleted(message);
  const hasUserContent = Boolean(rawText || message.sticker || message.contentCard || hasAttachments);
  const normalizedDialogTitle = String(dialogTitle || "").trim().toLowerCase();
  const titleLooksLikeDialog = Boolean(title && normalizedDialogTitle && title.trim().toLowerCase() === normalizedDialogTitle);
  const rawTextLooksLikeDialog = Boolean(rawText && normalizedDialogTitle && rawText.trim().toLowerCase() === normalizedDialogTitle);
  const renameType =
    type.includes("rename") ||
    type.includes("title") ||
    type.includes("name") ||
    type.includes("chat_renamed") ||
    type.includes("dialog_title");
  const renameTextHint = /(названи[ея]\s+чата\s+измен|переименовал|changed\s+chat\s+title)/i.test(rawText);

  if (deleted || message.sticker || message.contentCard || hasAttachments) return "";
  if (type.includes("avatar") || type.includes("photo")) return "Аватар чата обновлён";
  if (renameType || renameTextHint) {
    return rawText || (title ? "Название чата изменено на «" + title + "»" : "Название чата изменено");
  }
  if (!rawText && explicitTitle) {
    return "Название чата изменено на «" + explicitTitle + "»";
  }
  if (rawTextLooksLikeDialog) {
    return "Название чата изменено на «" + rawText + "»";
  }
  if (!rawText && title && titleLooksLikeDialog) {
    return "Название чата изменено на «" + title + "»";
  }
  if (type.includes("system") || type.includes("event") || type.includes("service")) {
    return rawText || title || "Системное событие";
  }
  // Некоторые backend-версии присылают событие переименования как пустое сообщение
  // с title/fromName. Такие записи должны быть системными (неинтерактивными).
  if (!hasUserContent && title) {
    if (titleLooksLikeDialog) return "Название чата изменено на «" + title + "»";
    if (!hasValidSender || explicitTitle) return title;
  }
  return "";
}

function normalizePinnedCandidate(candidate, messages = []) {
  if (!candidate) return null;
  const raw = candidate.message && typeof candidate.message === "object" ? candidate.message : candidate;
  const id = raw.messageId ?? raw.id ?? candidate.messageId ?? candidate.id;
  if (id == null) return null;
  const found = messages.find((message) => String(message?.id ?? message?.clientId ?? "") === String(id));
  return {
    messageId: String(id),
    scope: String(candidate.scope || raw.scope || candidate.pinScope || raw.pinScope || "self").toLowerCase(),
    text: String(candidate.text || raw.text || found?.text || buildMessagePreview(found) || "Закреплённое сообщение"),
    pinnedAt: candidate.pinnedAt || raw.pinnedAt || candidate.createdAt || raw.createdAt || null,
  };
}

export default function ChatUI({
  embedded = false,
  forcedDialogId = "",
  singleDialogMode = false,
  hideInfoPanel = false,
  hideDialogSettings = false,
  onMessagesChange = null,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { me } = useAuth();

  const {
    meId,
    dialogs,
    dialogsLoading,
    dialogsError,
    activeDialogId,
    activeDialog,
    activeTypingUsers,
    typingUsersByDialog,
    typingUsersMetaByDialog,
    markMessagesRead,
    openDialog,
    messages,
    messagesLoading,
    loadMore,
    sendMessage,
    notifyTyping,
    deleteMessage,
    editMessage,
    reactToMessage,
    addDialogMember,
    removeDialogMember,
    refreshDialogDetails,
  } = useChat();

  const myId = meId ?? me?.id ?? me?.userId ?? me?.user_id ?? null;
  const myRoleCode = normalizeRoleCode(me?.role ?? me?.roleCode ?? me?.role_code ?? me?.userRole ?? me?.user_role);
  const isPsychologistLike = CHAT_PSYCHOLOGIST_ROLE_CODES.has(myRoleCode) || CHAT_ADMIN_ROLE_CODES.has(myRoleCode);
  const canModerateVerification = CHAT_ADMIN_ROLE_CODES.has(myRoleCode);

  const openPsychologistProfile = React.useCallback(
    (event, target) => {
      event?.preventDefault();
      event?.stopPropagation();

      const source = target && typeof target === "object" ? target : { userId: target };
      const explicitRoute = String(source.route || "").trim();
      const route = explicitRoute
        ? explicitRoute
        : resolvePsychologistProfileRoute(
            source.profileUrl ?? source.psychologistProfileUrl ?? source.partnerProfileUrl,
            source.userId ?? source.psychologistUserId ?? source.partnerUserId ?? source.id
          ) || "";

      if (!route) return;

      if (isExternalHttpUrl(route)) {
        if (typeof window !== "undefined") window.location.assign(route);
        return;
      }

      if (route.startsWith("/")) {
        navigate(route);
        return;
      }

      const fallbackPath = buildPsychologistProfilePath(route);
      if (fallbackPath) navigate(fallbackPath);
    },
    [navigate]
  );

  const {
    search,
    setSearch,
    filter,
    setFilter,
    draft,
    setDraft,
    pendingFiles,
    setPendingFiles,
    pendingFilePreviews,
    setPendingFilePreviews,
    replyTo,
    setReplyTo,
    sending,
    setSending,
    loadingMore,
    setLoadingMore,
    isMobile,
    setIsMobile,
    sidebarHiddenMobile,
    setSidebarHiddenMobile,
    infoOpen,
    setInfoOpen,
    showScrollBottomFab,
    setShowScrollBottomFab,
    highlightMessageKey,
    setHighlightMessageKey,
    emojiPickerOpen,
    setEmojiPickerOpen,
    pickerMode,
    setPickerMode,
    reactionPickerMessage,
    setReactionPickerMessage,
    dragOver,
    setDragOver,
    confirmDeleteMessage,
    setConfirmDeleteMessage,
    selectedEmojiCategory,
    setSelectedEmojiCategory,
    selectedStickerCategory,
    setSelectedStickerCategory,
    editingMessage,
    setEditingMessage,
    mediaGallery,
    setMediaGallery,
    fileInputRef,
    textareaRef,
    messagesRef,
    loadingMoreRef,
    deepLinkOpenRef,
    deepLinkJumpRef,
    readMarkRef,
    draftLimitNoticeAtRef,
  } = useChatUIState();

  const [messageReadersState, setMessageReadersState] = React.useState({
    messageId: null,
    loading: false,
    error: "",
    readers: [],
  });
  const [reactionUsersState, setReactionUsersState] = React.useState({
    messageId: null,
    emoji: "",
    loading: false,
    error: "",
    users: [],
  });
  const [chatNotificationMutedByDialog, setChatNotificationMutedByDialog] = React.useState({});
  const [memberSearch, setMemberSearch] = React.useState("");
  const [memberSearchLoading, setMemberSearchLoading] = React.useState(false);
  const [memberSearchError, setMemberSearchError] = React.useState("");
  const [memberSearchResults, setMemberSearchResults] = React.useState([]);
  const [memberInvitePendingId, setMemberInvitePendingId] = React.useState(null);
  const [memberRemovePendingId, setMemberRemovePendingId] = React.useState(null);
  const [recommendPickerOpen, setRecommendPickerOpen] = React.useState(false);
  const [messageSearchOpen, setMessageSearchOpen] = React.useState(false);
  const [messageSearchQuery, setMessageSearchQuery] = React.useState("");
  const [messageSearchActiveIndex, setMessageSearchActiveIndex] = React.useState(0);
  const [chatSettingsOpen, setChatSettingsOpen] = React.useState(false);
  const [chatSettingsSaving, setChatSettingsSaving] = React.useState(false);
  const [chatSettingsDraft, setChatSettingsDraft] = React.useState({
    title: "",
    writeMode: CHAT_WRITE_MODE_ALL,
    mediaMode: CHAT_MEDIA_MODE_ALL,
    allowedUserIds: [],
    avatarFile: null,
    avatarPreview: "",
  });
  const [chatSettingsOverrides, setChatSettingsOverrides] = React.useState({});
  const [titleOverridesByDialog, setTitleOverridesByDialog] = React.useState({});
  const [avatarOverridesByDialog, setAvatarOverridesByDialog] = React.useState({});
  const [pinnedByDialog, setPinnedByDialog] = React.useState({});
  const [pinSavingKey, setPinSavingKey] = React.useState("");
  const [editingExistingAttachments, setEditingExistingAttachments] = React.useState([]);
  const [mobileActionsMessageKey, setMobileActionsMessageKey] = React.useState("");
  const [verificationActionStateByMessage, setVerificationActionStateByMessage] = React.useState({});
  const [verificationStatusByUserId, setVerificationStatusByUserId] = React.useState(() => readVerificationStatusCache());
  const verificationStatusFetchInFlightRef = React.useRef(new Set());
  const autoScrollDialogRef = React.useRef("");
  const lastAutoScrollMessageKeyRef = React.useRef("");

  // Particle effects (Telegram-like). Effect must start from the final reaction badge,
  // not from the floating menu button, so callers pass the badge element after state update.
  function createParticles(element, emoji) {
    if (!element) return;
    triggerReactionEffectFromElement(element, emoji || "❤️");
  }

  // Insert text at cursor position in textarea
  function insertText(textarea, text) {
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = textarea.value.substring(0, start);
    const after = textarea.value.substring(end);
    const newValue = before + text + after;
    const newCursorPos = start + text.length;

    setDraft(newValue);
    textarea.value = newValue;
    textarea.setSelectionRange(newCursorPos, newCursorPos);
    textarea.focus();
  }

  // Insert emoji at cursor position
  function insertEmoji(emoji) {
    if (textareaRef.current) {
      insertText(textareaRef.current, emoji);
    }
  }

  // Toggle emoji picker
  function toggleEmojiPicker() {
    setEmojiPickerOpen((prev) => !prev);
    setReactionPickerMessage(null);
    setRecommendPickerOpen(false);
    setMobileActionsMessageKey("");
  }

  // Handle emoji selection from picker
  function handleEmojiSelect(emoji) {
    insertEmoji(emoji);
    // Keep picker open for multiple selections, user can click outside to close
  }

  // Handle sticker selection. Stickers are sent as a dedicated payload and rendered as large emoji.
  const handleStickerSelect = React.useCallback(
    async (sticker) => {
      const text = buildChatStickerPayload(sticker);
      setEmojiPickerOpen(false);
      if (!text) return;
      if (!activeDialogId || !activeDialog || activeDialog.locked) return;

      setSending(true);
      try {
        await sendMessage({
          dialogId: activeDialogId,
          text,
          files: [],
          replyToMessageId: null,
        });
      } catch (error) {
        toast.error(error?.message || "Не удалось отправить стикер");
      } finally {
        setSending(false);
      }
    },
    [activeDialog, activeDialogId, sendMessage, setEmojiPickerOpen, setSending, toast]
  );

  const handleSendRecommendation = React.useCallback(
    async (card) => {
      const text = buildChatContentCardPayload(card);
      if (!text) {
        toast.error("Материал нельзя отправить: нет названия или типа");
        return;
      }
      if (!activeDialogId || !activeDialog || activeDialog.locked) {
        toast.error("Чат закрыт для новых сообщений");
        return;
      }

      setSending(true);
      try {
        await sendMessage({
          dialogId: activeDialogId,
          text,
          files: [],
          replyToMessageId: null,
        });
        setRecommendPickerOpen(false);
        toast.success("Рекомендация отправлена в чат");
      } catch (error) {
        toast.error(error?.message || "Не удалось отправить рекомендацию");
      } finally {
        setSending(false);
      }
    },
    [activeDialog, activeDialogId, sendMessage, setSending, toast]
  );

  function renderContentCard(card) {
    if (!card) return null;
    const href = safeExternalUrl(card.url || "");
    const inner = (
      <>
        <div className="chat-content-card__top">
          <span className="chat-content-card__icon">{card.icon || "✨"}</span>
          <span className="chat-content-card__label">{card.label || "Рекомендация"}</span>
        </div>
        <div className="chat-content-card__title">{card.title}</div>
        {card.subtitle ? <div className="chat-content-card__subtitle">{card.subtitle}</div> : null}
        {card.description ? <div className="chat-content-card__desc">{card.description}</div> : null}
        {href ? <div className="chat-content-card__action">{card.actionLabel || "Открыть"}</div> : null}
      </>
    );

    if (!href) {
      return <div className="chat-content-card">{inner}</div>;
    }

    if (href.startsWith("/")) {
      return (
        <a
          className="chat-content-card chat-content-card--link"
          href={href}
          onClick={(event) => {
            event.preventDefault();
            navigate(href);
          }}
        >
          {inner}
        </a>
      );
    }

    return (
      <a className="chat-content-card chat-content-card--link" href={href} target="_blank" rel="noreferrer">
        {inner}
      </a>
    );
  }

  // Show reaction picker close to the concrete action button/bubble.
  // Important: the picker is rendered once at the root of ChatUI, not inside a message bubble,
  // so it cannot disappear because of message id mismatch, clipping, or nested z-index contexts.
  function showReactionPicker(message, event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const target = event?.target instanceof Element ? event.target : null;
    const current = event?.currentTarget instanceof Element ? event.currentTarget : null;
    const row = target?.closest?.(".msg-row") || current?.closest?.(".msg-row");
    const actionButton =
      current?.closest?.(".msg-action-btn") ||
      target?.closest?.(".msg-action-btn") ||
      null;
    const bubble =
      target?.closest?.(".msg-bubble") ||
      row?.querySelector?.(".msg-bubble") ||
      null;

    const anchorRect =
      actionButton?.getBoundingClientRect?.() ||
      bubble?.getBoundingClientRect?.() ||
      row?.getBoundingClientRect?.() ||
      current?.getBoundingClientRect?.() ||
      null;

    const viewport = window.visualViewport;
    const viewportLeft = Number(viewport?.offsetLeft || 0);
    const viewportTop = Number(viewport?.offsetTop || 0);
    const vw = Number(viewport?.width || window.innerWidth || document.documentElement.clientWidth || 1280);
    const rawMouseX = Number(event?.clientX || 0);
    const mouseInsideAnchor =
      anchorRect && rawMouseX >= anchorRect.left && rawMouseX <= anchorRect.right;

    const preferredX = anchorRect
      ? actionButton
        ? anchorRect.left + anchorRect.width / 2
        : mouseInsideAnchor && rawMouseX > 0
          ? rawMouseX
          : anchorRect.left + anchorRect.width / 2
      : viewportLeft + vw / 2;
    const preferredTop = anchorRect ? anchorRect.top : viewportTop + Math.max(80, Number(event?.clientY || 120) - 12);
    const preferredBottom = anchorRect ? anchorRect.bottom : preferredTop + 40;
    const preferredWidth = actionButton ? 344 : anchorRect ? Math.max(264, Math.min(430, anchorRect.width + 132)) : 344;
    const preferredPlacement = preferredTop - viewportTop < 88 ? "below" : "above";
    const resolvedMessageKey = messageDomKey(message);

    setReactionPickerMessage({
      message,
      id: message?.id ?? null,
      clientId: message?.clientId ?? null,
      messageKey: resolvedMessageKey,
      x: preferredX,
      y: preferredTop,
      top: preferredTop,
      bottom: preferredBottom,
      width: preferredWidth,
      placement: preferredPlacement,
    });
    setEmojiPickerOpen(false);
    setMobileActionsMessageKey("");
  }

  React.useEffect(() => {
    if (!reactionPickerMessage) return undefined;
    const close = (event) => {
      if (event?.target?.closest?.(".reaction-picker, .msg-action-btn")) return;
      setReactionPickerMessage(null);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setReactionPickerMessage(null);
    };
    document.addEventListener("pointerdown", close, true);
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("pointerdown", close, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener("scroll", close, true);
    };
  }, [reactionPickerMessage, setReactionPickerMessage]);

  React.useEffect(() => {
    if (!isMobile && mobileActionsMessageKey) setMobileActionsMessageKey("");
  }, [isMobile, mobileActionsMessageKey]);

  React.useEffect(() => {
    setMobileActionsMessageKey("");
  }, [activeDialogId]);

  React.useEffect(() => {
    if (!mobileActionsMessageKey) return;
    const close = (event) => {
      if (event.target instanceof Element && event.target.closest(".msg-row.is-mobile-actions-open")) return;
      setMobileActionsMessageKey("");
    };
    document.addEventListener("pointerdown", close, true);
    return () => document.removeEventListener("pointerdown", close, true);
  }, [mobileActionsMessageKey]);

  // Handle reaction selection. The effect is launched from the reaction badge after the
  // optimistic/server update, so it appears exactly where the reaction was added.
  async function onReactionSelect(emoji) {
    if (!reactionPickerMessage) return;
    await onToggleReaction(reactionPickerMessage.message || reactionPickerMessage, emoji, true);
    setReactionPickerMessage(null);
  }

  const closeMessageReaders = React.useCallback(() => {
    setMessageReadersState((prev) => {
      if (prev.messageId == null) return prev;
      return {
        messageId: null,
        loading: false,
        error: "",
        readers: [],
      };
    });
  }, []);

  const onMessageStatusClick = React.useCallback(
    async (message, event) => {
      event?.preventDefault();
      event?.stopPropagation();

      const messageId = Number(message?.id);
      if (!isServerMessageId(messageId)) return;

      let shouldLoad = true;
      setMessageReadersState((prev) => {
        const isSame = String(prev.messageId ?? "") === String(messageId);
        if (isSame) {
          shouldLoad = false;
          return {
            messageId: null,
            loading: false,
            error: "",
            readers: [],
          };
        }
        return {
          messageId,
          loading: true,
          error: "",
          readers: [],
        };
      });

      if (!shouldLoad) return;

      try {
        const raw = await chatApi.getMessageReaders(messageId);
        const readers = extractMessageReaders(raw);
        setMessageReadersState((prev) => {
          if (String(prev.messageId ?? "") !== String(messageId)) return prev;
          return {
            messageId,
            loading: false,
            error: "",
            readers,
          };
        });
      } catch (error) {
        setMessageReadersState((prev) => {
          if (String(prev.messageId ?? "") !== String(messageId)) return prev;
          return {
            messageId,
            loading: false,
            error: error?.message || "Не удалось загрузить статусы прочтения",
            readers: [],
          };
        });
      }
    },
    []
  );

  const closeReactionUsers = React.useCallback(() => {
    setReactionUsersState({ messageId: null, emoji: "", loading: false, error: "", users: [] });
  }, []);

  const getReactionUsersFromMessage = React.useCallback((message, emoji) => {
    const reaction = (Array.isArray(message?.reactions) ? message.reactions : []).find((item) => item.emoji === emoji);
    const rawUsers = reaction?.users || reaction?.reactedUsers || reaction?.userList || [];
    if (!Array.isArray(rawUsers)) return [];
    return rawUsers.map((user, index) => {
      if (typeof user === "string") return { userId: user, name: user };
      return {
        userId: user?.userId ?? user?.id ?? `${emoji}-${index}`,
        name: user?.name || user?.fullName || user?.displayName || user?.username || `Участник ${index + 1}`,
        avatarUrl: user?.avatarUrl || user?.avatar || "",
      };
    });
  }, []);

  const onReactionUsersClick = React.useCallback(
    async (message, reaction, event) => {
      event?.preventDefault();
      event?.stopPropagation();
      const messageId = message?.id;
      const emoji = reaction?.emoji || "";
      if (!emoji || !isServerMessageId(messageId)) return;

      let shouldLoad = true;
      const localUsers = getReactionUsersFromMessage(message, emoji);
      setReactionUsersState((prev) => {
        const same = String(prev.messageId ?? "") === String(messageId) && prev.emoji === emoji;
        if (same) {
          shouldLoad = false;
          return { messageId: null, emoji: "", loading: false, error: "", users: [] };
        }
        return { messageId, emoji, loading: localUsers.length === 0, error: "", users: localUsers };
      });

      if (!shouldLoad || localUsers.length) return;

      try {
        if (typeof chatApi.getReactionUsers !== "function") {
          throw new Error("Backend пока не отдаёт список пользователей реакции");
        }
        const raw = await chatApi.getReactionUsers(messageId, emoji);
        const users = extractMessageReaders(raw).map((item, index) => ({
          userId: item.userId ?? item.id ?? `${emoji}-${index}`,
          name: item.name || item.fullName || item.displayName || `Участник ${index + 1}`,
          avatarUrl: item.avatarUrl || item.avatar || "",
        }));
        setReactionUsersState((prev) => {
          if (String(prev.messageId ?? "") !== String(messageId) || prev.emoji !== emoji) return prev;
          return { messageId, emoji, loading: false, error: "", users };
        });
      } catch (error) {
        setReactionUsersState((prev) => {
          if (String(prev.messageId ?? "") !== String(messageId) || prev.emoji !== emoji) return prev;
          return {
            messageId,
            emoji,
            loading: false,
            error: error?.message || "Не удалось загрузить список реакций",
            users: [],
          };
        });
      }
    },
    [getReactionUsersFromMessage]
  );

  // Drag and drop handlers
  function onDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!chatCanSendMedia) return;
    setDragOver(true);
  }

  function onDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && !chatCanSendMedia) {
      toast.info(chatMediaRestrictionText);
      return;
    }
    if (files.length > 0) {
      // Create a fake event object to reuse onPickFiles logic
      const fakeEvent = {
        target: {
          files: files,
          value: ''
        }
      };
      onPickFiles(fakeEvent);
    }
  }

  // Paste handler for clipboard images
  function onPaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        e.preventDefault();
        if (!chatCanSendMedia) {
          toast.info(chatMediaRestrictionText);
          return;
        }
        const file = item.getAsFile();
        if (file) {
          const fakeEvent = {
            target: {
              files: [file],
              value: ''
            }
          };
          onPickFiles(fakeEvent);
        }
        break;
      }
    }
  }

  const deepLink = React.useMemo(() => {
    const sp = new URLSearchParams(location.search || "");
    const forced = String(forcedDialogId || "").trim();
    const dialogId = forced || sp.get("dialogId") || sp.get("chatId") || sp.get("did") || "";
    const messageId = sp.get("messageId") || sp.get("msg") || sp.get("mid") || "";

    return {
      dialogId: dialogId.trim() || "",
      messageId: messageId.trim() || "",
    };
  }, [forcedDialogId, location.search]);

  React.useEffect(() => {
    if (embedded) return undefined;
    const html = document.documentElement.style.overflow;
    const body = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = html;
      document.body.style.overflow = body;
    };
  }, [embedded]);

  React.useEffect(() => {
    try {
      const savedInfoPanel = window.localStorage.getItem(CHAT_UI_INFO_PANEL_KEY);
      if (savedInfoPanel === "0" || savedInfoPanel === "1") {
        setInfoOpen(savedInfoPanel === "1");
      }
      const savedDialogSidebar = window.localStorage.getItem(CHAT_UI_DIALOG_SIDEBAR_KEY);
      if (savedDialogSidebar === "0" || savedDialogSidebar === "1") {
        setSidebarHiddenMobile(savedDialogSidebar === "1");
      }
    } catch {
      // ignore localStorage errors
    }
  }, [setInfoOpen, setSidebarHiddenMobile]);

  React.useEffect(() => {
    if (isMobile) return;
    try {
      window.localStorage.setItem(CHAT_UI_INFO_PANEL_KEY, infoOpen ? "1" : "0");
    } catch {
      // ignore localStorage errors
    }
  }, [infoOpen, isMobile]);

  React.useEffect(() => {
    try {
      window.localStorage.setItem(
        CHAT_UI_DIALOG_SIDEBAR_KEY,
        sidebarHiddenMobile ? "1" : "0"
      );
    } catch {
      // ignore localStorage errors
    }
  }, [sidebarHiddenMobile]);

  React.useEffect(() => {
    if (!isMobile) return;
    if (activeDialogId) return;
    if (singleDialogMode) {
      setSidebarHiddenMobile(true);
      return;
    }
    setSidebarHiddenMobile(false);
  }, [activeDialogId, isMobile, setSidebarHiddenMobile, singleDialogMode]);

  React.useEffect(() => {
    if (!singleDialogMode) return;
    setSidebarHiddenMobile(true);
    if (hideInfoPanel) setInfoOpen(false);
  }, [hideInfoPanel, setInfoOpen, setSidebarHiddenMobile, singleDialogMode]);

  React.useEffect(() => {
    let mediaQuery;
    try {
      mediaQuery = window.matchMedia("(max-width: 768px)");
    } catch {
      return undefined;
    }

    const handle = (event) => {
      const mobile = Boolean(event.matches);
      setIsMobile(mobile);
      if (mobile) {
        setInfoOpen(false);
      }
    };

    handle(mediaQuery);

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", handle);
    else mediaQuery.addListener(handle);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", handle);
      else mediaQuery.removeListener(handle);
    };
  }, [setInfoOpen, setIsMobile]);

  const dialogsFiltered = React.useMemo(() => {
    const query = search.trim().toLowerCase();

    return dialogs.filter((dialog) => {
      const title = getDialogTitle(dialog).toLowerCase();
      const preview = getDialogPreview(dialog).toLowerCase();
      const matchesSearch = !query || title.includes(query) || preview.includes(query);
      if (!matchesSearch) return false;

      if (filter === FILTER_CLIENTS) return !isGroupDialog(dialog);
      if (filter === FILTER_GROUPS) return isGroupDialog(dialog);
      if (filter === FILTER_ARCHIVE) return isArchivedDialog(dialog);
      return true;
    });
  }, [dialogs, filter, search]);

  const activeDialogBaseTitle = React.useMemo(() => getDialogTitle(activeDialog), [activeDialog]);
  const activeDialogTitle = React.useMemo(() => {
    const override = activeDialogId ? titleOverridesByDialog[String(activeDialogId)] : "";
    return override || activeDialogBaseTitle;
  }, [activeDialogBaseTitle, activeDialogId, titleOverridesByDialog]);
  const activeDialogAvatarUrl = React.useMemo(() => {
    const override = activeDialogId ? avatarOverridesByDialog[String(activeDialogId)] : "";
    const fallback = isAegisDialogCandidate(activeDialog, activeDialogTitle) ? AEGIS_AVATAR_URL : "";
    return override || activeDialog?.avatarUrl || activeDialog?.avatar || activeDialog?.imageUrl || fallback;
  }, [activeDialog, activeDialogId, activeDialogTitle, avatarOverridesByDialog]);
  const isAegisActionDialog = React.useMemo(
    () => isAegisDialogCandidate(activeDialog, activeDialogTitle),
    [activeDialog, activeDialogTitle]
  );
  const isAdminNotificationsOnlyDialog = React.useMemo(
    () => isAdminNotificationsDialog(activeDialog, activeDialogTitle),
    [activeDialog, activeDialogTitle]
  );
  const canManageVerificationActions = canModerateVerification && isAegisActionDialog;
  const activeDialogIsGroup = React.useMemo(() => isGroupDialog(activeDialog), [activeDialog]);

  const activeDialogMembers = React.useMemo(() => {
    if (Array.isArray(activeDialog?.members) && activeDialog.members.length) {
      const mapped = activeDialog.members
        .map((member) => {
          if (!member) return null;
          const userId = toUserId(member.userId ?? member.id);
          if (!userId) return null;
          const roleCode = normalizeRoleCode(member.roleCode ?? member.role ?? member.memberRole ?? member.chatRole);
          const isHost =
            member.isHost === true ||
            member.host === true ||
            member.owner === true ||
            (roleCode ? CHAT_HOST_ROLE_CODES.has(roleCode) : false);
          const isAdmin =
            member.isAdmin === true ||
            member.admin === true ||
            isHost ||
            (roleCode ? CHAT_ADMIN_ROLE_CODES.has(roleCode) : false);

          return {
            ...member,
            id: userId,
            userId,
            roleCode: roleCode || undefined,
            userRole: normalizeRoleCode(member.userRole ?? member.accountRole ?? member.profileRole) || undefined,
            isPsychologist:
              member.isPsychologist === true ||
              normalizeRoleCode(member.userRole ?? member.accountRole ?? member.profileRole) === "PSYCHOLOGIST" ||
              roleCode === "PSYCHOLOGIST",
            isHost,
            isAdmin,
          };
        })
        .filter(Boolean);

      if (mapped.length) return mapped;
    }

    if (!activeDialog) return [];

    return [
      {
        userId: toUserId(activeDialog.partnerUserId) || toUserId(activeDialog.id) || null,
        id: toUserId(activeDialog.partnerUserId) || toUserId(activeDialog.id) || null,
        name: activeDialogTitle,
        avatarUrl: activeDialog.avatarUrl || "",
        profileUrl: activeDialog.partnerProfileUrl || "",
        isOnline: Boolean(activeDialog.online),
        roleCode: undefined,
        userRole: normalizeRoleCode(activeDialog.partnerRole) || undefined,
        isPsychologist: normalizeRoleCode(activeDialog.partnerRole) === "PSYCHOLOGIST",
        isHost: false,
        isAdmin: false,
      },
    ];
  }, [activeDialog, activeDialogTitle]);

  const activeDialogMemberIds = React.useMemo(() => {
    const out = new Set();
    activeDialogMembers.forEach((member) => {
      const id = toUserId(member?.userId ?? member?.id);
      if (id) out.add(String(id));
    });
    return out;
  }, [activeDialogMembers]);

  const activeDialogHostId = React.useMemo(() => {
    const hostFromDialog = toUserId(activeDialog?.hostUserId);
    if (hostFromDialog) return hostFromDialog;

    for (const member of activeDialogMembers) {
      const roleCode = normalizeRoleCode(member?.roleCode);
      if (member?.isHost === true || (roleCode && CHAT_HOST_ROLE_CODES.has(roleCode))) {
        return toUserId(member?.userId ?? member?.id);
      }
    }
    return null;
  }, [activeDialog, activeDialogMembers]);

  const myMember = React.useMemo(() => {
    if (!myId) return null;
    return (
      activeDialogMembers.find((member) => Number(member?.userId ?? member?.id) === Number(myId)) || null
    );
  }, [activeDialogMembers, myId]);

  const canManageMembers = React.useMemo(() => {
    if (!activeDialogIsGroup) return false;

    if (activeDialog?.canManageMembers != null) {
      return Boolean(activeDialog.canManageMembers);
    }

    if (activeDialog?.isHost === true || activeDialog?.isAdmin === true) return true;

    const myRoleCode = normalizeRoleCode(activeDialog?.myRole ?? myMember?.roleCode);
    if (myRoleCode && CHAT_ADMIN_ROLE_CODES.has(myRoleCode)) return true;

    if (myMember?.isHost === true || myMember?.isAdmin === true) return true;

    if (activeDialogHostId && myId && Number(activeDialogHostId) === Number(myId)) return true;

    return false;
  }, [activeDialog, activeDialogHostId, activeDialogIsGroup, myId, myMember]);

  const activeDialogSettings = React.useMemo(() => {
    const local = activeDialogId ? chatSettingsOverrides[String(activeDialogId)] : {};
    return extractDialogSettings(activeDialog, local);
  }, [activeDialog, activeDialogId, chatSettingsOverrides]);

  const canManageChat = React.useMemo(() => {
    if (!activeDialog) return false;
    if (canManageMembers) return true;
    if (activeDialog?.isHost === true || activeDialog?.isAdmin === true) return true;
    const myRoleCode = normalizeRoleCode(activeDialog?.myRole ?? myMember?.roleCode);
    if (myRoleCode && CHAT_ADMIN_ROLE_CODES.has(myRoleCode)) return true;
    if (myMember?.isHost === true || myMember?.isAdmin === true) return true;
    return Boolean(activeDialogHostId && myId && Number(activeDialogHostId) === Number(myId));
  }, [activeDialog, activeDialogHostId, canManageMembers, myId, myMember]);

  const chatCanWrite = React.useMemo(() => {
    if (!activeDialog || activeDialog.locked) return false;
    if (isAdminNotificationsOnlyDialog) return false;
    const sources = [
      activeDialog,
      activeDialog?.permissions && typeof activeDialog.permissions === "object" ? activeDialog.permissions : null,
      activeDialog?.settings && typeof activeDialog.settings === "object" ? activeDialog.settings : null,
      activeDialog?.chatSettings && typeof activeDialog.chatSettings === "object" ? activeDialog.chatSettings : null,
      myMember,
    ].filter(Boolean);

    let muted = false;

    for (const source of sources) {
      if (source?.writeMuted === true || source?.isWriteMuted === true || source?.write_muted === true || source?.is_write_muted === true) {
        muted = true;
      }
      if (
        source?.sendDisabled === true ||
        source?.send_disabled === true ||
        source?.writeDisabled === true ||
        source?.write_disabled === true ||
        source?.mutedForWrite === true ||
        source?.muted_for_write === true ||
        source?.blockedFromSending === true ||
        source?.blocked_from_sending === true
      ) {
        muted = true;
      }
      if (source?.canWrite === false || source?.can_write === false || source?.canSend === false || source?.can_send === false) {
        muted = true;
      }

    }

    if (muted) return false;
    if (!activeDialogIsGroup) return true;
    if (canManageChat) return true;
    if (activeDialogSettings.writeMode === CHAT_WRITE_MODE_HOST_ONLY) return false;
    if (activeDialogSettings.writeMode === CHAT_WRITE_MODE_SELECTED) {
      return Boolean(myId && activeDialogSettings.allowedUserIds.some((id) => Number(id) === Number(myId)));
    }
    return true;
  }, [activeDialog, activeDialogIsGroup, activeDialogSettings.allowedUserIds, activeDialogSettings.writeMode, canManageChat, isAdminNotificationsOnlyDialog, myId, myMember]);

  const chatMuteNotice = React.useMemo(() => {
    if (!activeDialog || chatCanWrite) return "";
    const sources = [
      activeDialog,
      activeDialog?.permissions && typeof activeDialog.permissions === "object" ? activeDialog.permissions : null,
      activeDialog?.settings && typeof activeDialog.settings === "object" ? activeDialog.settings : null,
      activeDialog?.chatSettings && typeof activeDialog.chatSettings === "object" ? activeDialog.chatSettings : null,
      myMember,
    ].filter(Boolean);

    let muted = false;
    let mutedUntil = null;
    let mutedReason = "";
    for (const source of sources) {
      if (
        source?.writeMuted === true ||
        source?.isWriteMuted === true ||
        source?.write_muted === true ||
        source?.is_write_muted === true ||
        source?.sendDisabled === true ||
        source?.send_disabled === true ||
        source?.writeDisabled === true ||
        source?.write_disabled === true ||
        source?.mutedForWrite === true ||
        source?.muted_for_write === true ||
        source?.blockedFromSending === true ||
        source?.blocked_from_sending === true ||
        source?.canWrite === false ||
        source?.can_write === false ||
        source?.canSend === false ||
        source?.can_send === false
      ) {
        muted = true;
      }
      if (!mutedUntil) {
        mutedUntil =
          source?.writeMutedUntil ??
          source?.write_muted_until ??
          source?.mutedUntil ??
          source?.muted_until ??
          source?.sendMutedUntil ??
          source?.send_muted_until ??
          source?.blockedUntil ??
          source?.blocked_until ??
          null;
      }
      if (!mutedReason) {
        mutedReason = String(
          source?.writeMuteReason ??
            source?.write_mute_reason ??
            source?.muteReason ??
            source?.mute_reason ??
            source?.restrictionReason ??
            source?.restriction_reason ??
            ""
        ).trim();
      }
    }

    if (!muted) return "";
    if (mutedReason) return mutedReason;
    const untilLabel = formatRestrictionDateTime(mutedUntil);
    if (untilLabel) return `Вы не можете писать в этот чат до ${untilLabel}`;
    return "Вы временно не можете отправлять сообщения в этот чат";
  }, [activeDialog, chatCanWrite, myMember]);

  const chatCanSendMedia = React.useMemo(() => {
    if (!chatCanWrite) return false;
    if (!activeDialogIsGroup) return true;
    if (activeDialogSettings.mediaMode === CHAT_MEDIA_MODE_DISABLED) return false;
    if (activeDialogSettings.mediaMode === CHAT_MEDIA_MODE_HOST_ONLY && !canManageChat) return false;
    return true;
  }, [activeDialogIsGroup, activeDialogSettings.mediaMode, canManageChat, chatCanWrite]);

  const chatWriteRestrictionText = React.useMemo(() => {
    if (activeDialog?.locked) return "Чат закрыт для новых сообщений";
    if (isAdminNotificationsOnlyDialog) return "Этот чат только для уведомлений Aegis. Отправка сообщений отключена.";
    if (chatMuteNotice) return chatMuteNotice;
    if (chatCanWrite) return "";
    if (activeDialogSettings.writeMode === CHAT_WRITE_MODE_HOST_ONLY) return "Сейчас писать может только хост чата";
    if (activeDialogSettings.writeMode === CHAT_WRITE_MODE_SELECTED) return "Сейчас писать могут только выбранные участники";
    return "Отправка сообщений ограничена хостом";
  }, [activeDialog, activeDialogSettings.writeMode, chatCanWrite, chatMuteNotice, isAdminNotificationsOnlyDialog]);

  const composerPlaceholder = React.useMemo(() => {
    if (editingMessage) return "Редактировать сообщение...";
    if (embedded || singleDialogMode) return chatWriteRestrictionText || "Сообщение...";
    return chatWriteRestrictionText || "Написать сообщение...";
  }, [chatWriteRestrictionText, editingMessage, embedded, singleDialogMode]);

  const chatMediaRestrictionText = React.useMemo(() => {
    if (chatCanSendMedia) return "";
    if (!chatCanWrite) return chatWriteRestrictionText || "Отправка сообщений ограничена";
    if (activeDialogSettings.mediaMode === CHAT_MEDIA_MODE_HOST_ONLY) return "Медиа сейчас может отправлять только хост чата";
    if (activeDialogSettings.mediaMode === CHAT_MEDIA_MODE_DISABLED) return "Отправка медиа в этом чате отключена";
    return "Отправка медиа ограничена";
  }, [activeDialogSettings.mediaMode, chatCanSendMedia, chatCanWrite, chatWriteRestrictionText]);

  const isMemberPsychologist = React.useCallback((member) => {
    if (!member) return false;
    if (member.isPsychologist === true) return true;

    const userRole = normalizeRoleCode(member.userRole ?? member.accountRole ?? member.profileRole);
    if (userRole && CHAT_PSYCHOLOGIST_ROLE_CODES.has(userRole)) return true;

    const roleCode = normalizeRoleCode(member.roleCode);
    if (roleCode && CHAT_PSYCHOLOGIST_ROLE_CODES.has(roleCode)) return true;

    return false;
  }, []);

  const activeDialogPsychologistId = React.useMemo(() => {
    const explicitId = toUserId(activeDialog?.psychologistUserId);
    if (explicitId) return explicitId;

    const memberCandidate = activeDialogMembers.filter(isMemberPsychologist);
    if (memberCandidate.length) {
      const preferred =
        memberCandidate.find((member) => {
          const id = toUserId(member?.userId ?? member?.id);
          if (!id) return false;
          return myId == null || Number(id) !== Number(myId);
        }) || memberCandidate[0];
      const id = toUserId(preferred?.userId ?? preferred?.id);
      if (id) return id;
    }

    if (!activeDialogIsGroup) {
      const partnerId = toUserId(activeDialog?.partnerUserId);
      if (!partnerId) return null;

      // Для клиента в личном диалоге собеседник и есть психолог,
      // даже если backend вернул нестандартный partnerRole.
      if (!isPsychologistLike) return partnerId;

      const partnerRole = normalizeRoleCode(activeDialog?.partnerRole);
      if (partnerRole && CHAT_PSYCHOLOGIST_ROLE_CODES.has(partnerRole)) {
        return partnerId;
      }
    }

    return null;
  }, [activeDialog, activeDialogIsGroup, activeDialogMembers, isMemberPsychologist, isPsychologistLike, myId]);

  const activeDialogPsychologistTarget = React.useMemo(() => {
    if (!activeDialog) return null;

    const explicitProfileUrl = String(
      activeDialog?.psychologistProfileUrl || activeDialog?.partnerProfileUrl || ""
    ).trim();
    if (activeDialogPsychologistId || explicitProfileUrl) {
      return {
        userId: activeDialogPsychologistId ?? null,
        profileUrl: explicitProfileUrl || "",
      };
    }

    const memberCandidate = activeDialogMembers.filter(isMemberPsychologist);
    if (memberCandidate.length) {
      const preferred =
        memberCandidate.find((member) => {
          const id = toUserId(member?.userId ?? member?.id);
          if (!id) return false;
          return myId == null || Number(id) !== Number(myId);
        }) || memberCandidate[0];

      return {
        userId: preferred?.userId ?? preferred?.id ?? null,
        profileUrl: String(
          preferred?.profileUrl || preferred?.psychologistProfileUrl || preferred?.partnerProfileUrl || ""
        ).trim(),
      };
    }

    return null;
  }, [activeDialog, activeDialogMembers, activeDialogPsychologistId, isMemberPsychologist, myId]);

  const activeDialogPsychologistRoute = React.useMemo(() => {
    if (!activeDialogPsychologistTarget) return "";
    return (
      resolvePsychologistProfileRoute(
        activeDialogPsychologistTarget.profileUrl,
        activeDialogPsychologistTarget.userId
      ) || ""
    );
  }, [activeDialogPsychologistTarget]);

  React.useEffect(() => {
    setChatSettingsDraft({
      title: activeDialogTitle || "",
      writeMode: activeDialogSettings.writeMode,
      mediaMode: activeDialogSettings.mediaMode,
      allowedUserIds: activeDialogSettings.allowedUserIds,
      avatarFile: null,
      avatarPreview: "",
    });
    setChatSettingsOpen(false);
    setMessageSearchOpen(false);
    setMessageSearchQuery("");
    setMessageSearchActiveIndex(0);
  }, [activeDialogId, activeDialogTitle, activeDialogSettings.writeMode, activeDialogSettings.mediaMode, activeDialogSettings.allowedUserIds]);

  const isMemberHost = React.useCallback(
    (member) => {
      if (!member) return false;
      const memberId = toUserId(member.userId ?? member.id);
      if (memberId && activeDialogHostId && Number(memberId) === Number(activeDialogHostId)) return true;
      const roleCode = normalizeRoleCode(member.roleCode);
      if (roleCode && CHAT_HOST_ROLE_CODES.has(roleCode)) return true;
      return member.isHost === true || member.host === true || member.owner === true;
    },
    [activeDialogHostId]
  );

  const isMemberAdmin = React.useCallback(
    (member) => {
      if (!member) return false;
      if (isMemberHost(member)) return true;
      const roleCode = normalizeRoleCode(member.roleCode);
      if (roleCode && CHAT_ADMIN_ROLE_CODES.has(roleCode)) return true;
      return member.isAdmin === true || member.admin === true;
    },
    [isMemberHost]
  );

  const orderedMessages = React.useMemo(() => {
    return [...(Array.isArray(messages) ? messages : [])].sort(
      (a, b) => toTimestamp(a?.createdAt) - toTimestamp(b?.createdAt)
    );
  }, [messages]);

  const verificationResolvedByUserIdFromLocalActions = React.useMemo(() => {
    const out = {};
    Object.values(verificationActionStateByMessage || {}).forEach((item) => {
      const userId = toUserId(item?.userId);
      const resolved = normalizeVerificationResolution(item?.resolved || "");
      if (!userId || !resolved) return;
      out[String(userId)] = resolved;
    });
    return out;
  }, [verificationActionStateByMessage]);

  React.useEffect(() => {
    writeVerificationStatusCache(verificationStatusByUserId);
  }, [verificationStatusByUserId]);

  React.useEffect(() => {
    if (!canManageVerificationActions) return;
    const now = Date.now();
    const userIds = new Set();
    orderedMessages.forEach((message) => {
      const action = extractPsychologistVerificationAction(message);
      const userId = toUserId(action?.userId);
      if (userId) userIds.add(String(userId));
    });
    const pendingLoad = Array.from(userIds).filter((userId) => {
      if (verificationStatusFetchInFlightRef.current.has(userId)) return false;
      const known = verificationStatusByUserId[userId];
      const status = String(known?.status || "").trim();
      const resolved = isVerificationResolvedStatus(status);
      if (resolved) return false;
      const fetchedAt = Number(known?.fetchedAt || 0);
      if (!fetchedAt) return true;
      return now - fetchedAt > 10_000;
    });
    if (!pendingLoad.length) return;

    let cancelled = false;
    pendingLoad.forEach((userId) => {
      verificationStatusFetchInFlightRef.current.add(userId);
      psychologistVerificationApi
        .getOne(userId)
        .then((raw) => {
          if (cancelled) return;
          const item = pickVerificationItem(raw);
          const status = String(
            item?.verificationStatus ||
              item?.psychologistVerificationStatus ||
              item?.status ||
              ""
          )
            .trim()
            .toUpperCase();
          const reviewerName = String(
            item?.reviewedByUserName ||
              item?.reviewerName ||
              item?.verifiedByName ||
              ""
          ).trim();
          const reviewerUserId = toUserId(
            item?.reviewedByUserId ?? item?.reviewerUserId ?? item?.verifiedByUserId
          );
          setVerificationStatusByUserId((prev) => ({
            ...mergeVerificationStatusCacheEntry(prev, userId, {
              status,
              reviewerName,
              reviewerUserId,
              fetchedAt: Date.now(),
            }),
          }));
        })
        .catch(() => {
          // no-op: keep buttons available if status lookup fails
          setVerificationStatusByUserId((prev) => ({
            ...prev,
            [userId]: {
              ...(prev[userId] || {}),
              fetchedAt: Date.now(),
            },
          }));
        })
        .finally(() => {
          verificationStatusFetchInFlightRef.current.delete(userId);
        });
    });

    return () => {
      cancelled = true;
    };
  }, [canManageVerificationActions, orderedMessages, verificationStatusByUserId]);

  React.useEffect(() => {
    if (typeof onMessagesChange !== "function") return;
    onMessagesChange({
      dialogId: activeDialogId ? String(activeDialogId) : "",
      messages: orderedMessages,
    });
  }, [activeDialogId, onMessagesChange, orderedMessages]);

  const messageSearchResults = React.useMemo(() => {
    const query = messageSearchQuery.trim().toLowerCase();
    if (!query) return [];
    return orderedMessages.filter((message) => messagePlainText(message).toLowerCase().includes(query));
  }, [messageSearchQuery, orderedMessages]);

  React.useEffect(() => {
    setMessageSearchActiveIndex((prev) => {
      if (!messageSearchResults.length) return 0;
      return Math.min(Math.max(0, prev), messageSearchResults.length - 1);
    });
  }, [messageSearchResults.length]);

  const activePinnedMessage = React.useMemo(() => {
    const localPinned = activeDialogId ? pinnedByDialog[String(activeDialogId)] : null;
    if (localPinned) return normalizePinnedCandidate(localPinned, orderedMessages);

    const backendPinned = Array.isArray(activeDialog?.pinnedMessages)
      ? activeDialog.pinnedMessages[0]
      : activeDialog?.pinnedMessage || activeDialog?.pinned || null;
    return normalizePinnedCandidate(backendPinned, orderedMessages);
  }, [activeDialog, activeDialogId, orderedMessages, pinnedByDialog]);

  const messageRows = React.useMemo(() => {
    const rows = [];
    let prevMessage = null;
    let prevDate = "";

    orderedMessages.forEach((message) => {
      const dateKey = formatDateKey(message?.createdAt);
      if (dateKey !== prevDate) {
        prevDate = dateKey;
        rows.push({
          type: "date",
          key: `date-${dateKey}`,
          label: formatDateSeparator(message?.createdAt),
        });
      }

      const mine = isMineMessage(message, myId);

      const prevMine = prevMessage ? isMineMessage(prevMessage, myId) : null;
      const sameSender =
        prevMessage &&
        prevMine === mine &&
        String(prevMessage?.fromUserId ?? prevMessage?.fromName ?? "") ===
          String(message?.fromUserId ?? message?.fromName ?? "");

      const closeByTime =
        prevMessage && Math.abs(toTimestamp(message?.createdAt) - toTimestamp(prevMessage?.createdAt)) <= 6 * 60 * 1000;

      const grouped = Boolean(prevMessage && sameSender && closeByTime);

      rows.push({
        type: "message",
        key: `message-${messageDomKey(message)}`,
        message,
        mine,
        grouped,
        firstInGroup: !grouped,
      });

      prevMessage = message;
    });

    return rows;
  }, [orderedMessages, myId]);

  const sharedFiles = React.useMemo(() => {
    const map = new Map();

    orderedMessages.forEach((message) => {
      const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
      attachments.forEach((attachment) => {
        const key = String(attachment?.id || attachment?.url || attachment?.name || "");
        if (!key || map.has(key)) return;
        map.set(key, {
          ...attachment,
          messageId: message?.id || null,
          clientId: message?.clientId || null,
          messageKey: messageDomKey(message),
          createdAt: message?.createdAt,
        });
      });
    });

    return Array.from(map.values());
  }, [orderedMessages]);

  const imageFiles = React.useMemo(() => sharedFiles, [sharedFiles]);

  const mediaAttachmentKey = React.useCallback(
    (attachment) => String(attachment?.id || attachment?.url || attachment?.name || ""),
    []
  );

  const openMediaAtAttachment = React.useCallback(
    (attachment) => {
      if (!attachment) return;
      const key = mediaAttachmentKey(attachment);
      if (!key) return;

      const index = imageFiles.findIndex((item) => mediaAttachmentKey(item) === key);
      if (index < 0) return;
      setMediaGallery({ images: imageFiles, index });
    },
    [imageFiles, mediaAttachmentKey, setMediaGallery]
  );

  const typingText = React.useMemo(
    () => buildTypingText({ activeDialog, activeTypingUsers }),
    [activeDialog, activeTypingUsers]
  );

  const buildDialogTypingPreview = React.useCallback(
    (dialog) => {
      const dialogId = String(dialog?.id || "");
      if (!dialogId) return "";

      const ids = Array.isArray(typingUsersByDialog?.get(dialogId))
        ? typingUsersByDialog.get(dialogId)
        : [];
      const meta = Array.isArray(typingUsersMetaByDialog?.get(dialogId))
        ? typingUsersMetaByDialog.get(dialogId)
        : [];

      if (!ids.length) {
        return dialog?.typing ? "печатает..." : "";
      }

      const namesFromMeta = meta
        .map((entry) => String(entry?.name || "").trim())
        .filter(Boolean);
      const namesFromMembers = ids
        .map((id) => {
          const member = (Array.isArray(dialog?.members) ? dialog.members : []).find(
            (item) => Number(item?.userId ?? item?.id) === Number(id)
          );
          return String(member?.name || member?.fullName || "").trim();
        })
        .filter(Boolean);
      const names = [...namesFromMeta, ...namesFromMembers].filter(
        (name, index, arr) => arr.indexOf(name) === index
      );

      if (!names.length) {
        return ids.length > 1 ? `${ids.length} участников печатают...` : "печатает...";
      }
      if (names.length === 1) return `${names[0]} печатает...`;
      if (names.length === 2) return `${names[0]} и ${names[1]} печатают...`;
      return `${names[0]} и еще ${names.length - 1} печатают...`;
    },
    [typingUsersByDialog, typingUsersMetaByDialog]
  );

  const selectDialog = React.useCallback(
    (dialogId) => {
      if (!dialogId) return;
      openDialog(dialogId);
      if (isMobile) {
        setSidebarHiddenMobile(true);
      }
    },
    [isMobile, openDialog, setSidebarHiddenMobile]
  );

  const showSidebar = React.useCallback(() => {
    setSidebarHiddenMobile(false);
    setInfoOpen(false);
  }, [setInfoOpen, setSidebarHiddenMobile]);

  React.useEffect(() => {
    setMemberSearch("");
    setMemberSearchLoading(false);
    setMemberSearchError("");
    setMemberSearchResults([]);
    setMemberInvitePendingId(null);
    setMemberRemovePendingId(null);
  }, [activeDialogId]);

  React.useEffect(() => {
    if (!infoOpen) return;
    if (!activeDialogId) return;
    if (!activeDialogIsGroup) return;
    refreshDialogDetails(activeDialogId, { force: false });
  }, [activeDialogId, activeDialogIsGroup, infoOpen, refreshDialogDetails]);

  React.useEffect(() => {
    if (!infoOpen || !activeDialogId || !activeDialogIsGroup || !canManageMembers) {
      setMemberSearchLoading(false);
      setMemberSearchError("");
      setMemberSearchResults([]);
      return undefined;
    }

    const query = memberSearch.trim();
    if (query.length < 2) {
      setMemberSearchLoading(false);
      setMemberSearchError("");
      setMemberSearchResults([]);
      return undefined;
    }

    if (typeof chatApi.searchUsers !== "function") {
      setMemberSearchLoading(false);
      setMemberSearchError("Поиск пользователей недоступен");
      setMemberSearchResults([]);
      return undefined;
    }

    let cancelled = false;
    const timerId = window.setTimeout(async () => {
      setMemberSearchLoading(true);
      setMemberSearchError("");
      try {
        const raw = await chatApi.searchUsers(query);
        if (cancelled) return;

        const result = (Array.isArray(raw) ? raw : [])
          .map(normalizeSearchUser)
          .filter(Boolean)
          .filter((user) => !activeDialogMemberIds.has(String(user.id)))
          .filter((user) => (myId ? Number(user.id) !== Number(myId) : true));

        setMemberSearchResults(result);
      } catch (error) {
        if (cancelled) return;
        setMemberSearchResults([]);
        setMemberSearchError(error?.message || "Не удалось найти пользователей");
      } finally {
        if (!cancelled) setMemberSearchLoading(false);
      }
    }, 260);

    return () => {
      cancelled = true;
      window.clearTimeout(timerId);
    };
  }, [activeDialogId, activeDialogIsGroup, activeDialogMemberIds, canManageMembers, infoOpen, memberSearch, myId]);

  const onInviteMember = React.useCallback(
    async (user) => {
      const userId = toUserId(user?.id);
      if (!activeDialogId || !userId) return;

      setMemberInvitePendingId(userId);
      try {
        await addDialogMember(activeDialogId, userId);
        setMemberSearchResults((prev) => prev.filter((item) => Number(item?.id) !== Number(userId)));
        toast.success(`Пользователь ${user?.name || userId} приглашён`);
      } catch (error) {
        toast.error(error?.message || "Не удалось добавить участника");
      } finally {
        setMemberInvitePendingId(null);
      }
    },
    [activeDialogId, addDialogMember, toast]
  );

  const onRemoveMember = React.useCallback(
    async (member) => {
      const memberId = toUserId(member?.userId ?? member?.id);
      if (!activeDialogId || !memberId) return;
      if (myId && Number(memberId) === Number(myId)) return;
      if (isMemberHost(member)) return;

      setMemberRemovePendingId(memberId);
      try {
        await removeDialogMember(activeDialogId, memberId);
        toast.success("Участник удалён из чата");
      } catch (error) {
        toast.error(error?.message || "Не удалось удалить участника");
      } finally {
        setMemberRemovePendingId(null);
      }
    },
    [activeDialogId, isMemberHost, myId, removeDialogMember, toast]
  );

  const scrollToBottom = React.useCallback((smooth = false) => {
    const element = messagesRef.current;
    if (!element) return;
    element.scrollTo({ top: element.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, [messagesRef]);

  const jumpToMessage = React.useCallback((messageId) => {
    if (!messageId) return;
    const key = safeDomId(messageId);
    const node = document.getElementById(`chat-msg-${key}`);
    if (!node) return;

    node.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightMessageKey(key);
    window.setTimeout(() => setHighlightMessageKey(""), 1600);
  }, [setHighlightMessageKey]);

  const jumpToSearchResult = React.useCallback(
    (index) => {
      if (!messageSearchResults.length) return;
      const safeIndex = Math.min(Math.max(Number(index) || 0, 0), messageSearchResults.length - 1);
      const message = messageSearchResults[safeIndex];
      setMessageSearchActiveIndex(safeIndex);
      jumpToMessage(messageDomKey(message));
    },
    [jumpToMessage, messageSearchResults]
  );

  const goToPrevSearchResult = React.useCallback(() => {
    if (!messageSearchResults.length) return;
    const next = messageSearchActiveIndex <= 0 ? messageSearchResults.length - 1 : messageSearchActiveIndex - 1;
    jumpToSearchResult(next);
  }, [jumpToSearchResult, messageSearchActiveIndex, messageSearchResults.length]);

  const goToNextSearchResult = React.useCallback(() => {
    if (!messageSearchResults.length) return;
    const next = messageSearchActiveIndex >= messageSearchResults.length - 1 ? 0 : messageSearchActiveIndex + 1;
    jumpToSearchResult(next);
  }, [jumpToSearchResult, messageSearchActiveIndex, messageSearchResults.length]);

  React.useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "auto";
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
  }, [draft, textareaRef]);

  React.useEffect(() => {
    if (dialogsLoading) return;
    if (!dialogs.length) return;
    if (deepLinkOpenRef.current) return;

    if (deepLink.dialogId) {
      const target = dialogs.find((item) => String(item.id) === String(deepLink.dialogId));
      if (target) {
        selectDialog(target.id);
        deepLinkOpenRef.current = true;
        return;
      }
    }

  }, [activeDialogId, deepLink.dialogId, dialogs, dialogsLoading, selectDialog, deepLinkOpenRef]);

  React.useEffect(() => {
    if (!activeDialogId || !deepLink.messageId) return;
    if (deepLink.dialogId && String(deepLink.dialogId) !== String(activeDialogId)) return;
    if (messagesLoading) return;
    if (deepLinkJumpRef.current) return;

    const timer = window.setTimeout(() => {
      jumpToMessage(deepLink.messageId);
      deepLinkJumpRef.current = true;
    }, 240);

    return () => window.clearTimeout(timer);
  }, [activeDialogId, deepLink.dialogId, deepLink.messageId, jumpToMessage, messagesLoading, messageRows.length, deepLinkJumpRef]);

  const markVisibleUnreadMessages = React.useCallback(() => {
    if (!activeDialogId) return;
    const container = messagesRef.current;
    if (!container) return;

    const unreadMap = new Map(
      orderedMessages
        .filter((message) => {
          if (isMessageDeleted(message)) return false;
          if (isMineMessage(message, myId)) return false;
          if (isReadByMe(message, isPsychologistLike)) return false;
          const messageId = Number(message?.id);
          return Number.isFinite(messageId) && messageId > 0;
        })
        .map((message) => [Number(message.id), message])
    );

    if (!unreadMap.size) return;

    const containerRect = container.getBoundingClientRect();
    const ids = [];
    const nodes = container.querySelectorAll(".msg-row[data-message-id]");
    nodes.forEach((node) => {
      const rawId = node.getAttribute("data-message-id");
      const messageId = Number(rawId);
      if (!Number.isFinite(messageId) || !unreadMap.has(messageId)) return;

      const rect = node.getBoundingClientRect();
      const visiblePx = Math.min(rect.bottom, containerRect.bottom) - Math.max(rect.top, containerRect.top);
      const requiredVisiblePx = Math.max(20, Math.min(rect.height * 0.35, 60));
      if (visiblePx < requiredVisiblePx) return;
      ids.push(messageId);
    });

    if (!ids.length) {
      const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceToBottom <= 96) {
        for (let index = orderedMessages.length - 1; index >= 0; index -= 1) {
          const messageId = Number(orderedMessages[index]?.id);
          if (!Number.isFinite(messageId) || messageId <= 0) continue;
          if (!unreadMap.has(messageId)) continue;
          ids.push(messageId);
          break;
        }
      }
    }

    if (!ids.length) return;

    ids.sort((a, b) => a - b);
    const signature = `${activeDialogId}:${ids[0]}:${ids[ids.length - 1]}:${ids.length}`;
    if (readMarkRef.current === signature) return;
    readMarkRef.current = signature;

    markMessagesRead({ dialogId: activeDialogId, messageIds: ids });
  }, [activeDialogId, isPsychologistLike, markMessagesRead, messagesRef, myId, orderedMessages, readMarkRef]);

  React.useEffect(() => {
    readMarkRef.current = "";
  }, [activeDialogId, readMarkRef]);

  React.useEffect(() => {
    if (!activeDialogId || !orderedMessages.length) return;
    const raf = window.requestAnimationFrame(() => {
      markVisibleUnreadMessages();
    });
    const timer = window.setTimeout(() => {
      markVisibleUnreadMessages();
    }, 260);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [activeDialogId, markVisibleUnreadMessages, orderedMessages]);

  React.useEffect(() => {
    if (!activeDialogId) return undefined;
    const markSoon = () => {
      window.setTimeout(() => markVisibleUnreadMessages(), 160);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") markSoon();
    };
    window.addEventListener("focus", markSoon);
    window.addEventListener("resize", markSoon);
    document.addEventListener("visibilitychange", onVisibility);
    // Фоновая страховка без частого спама: новые сообщения дополнительно помечаются из useChat при открытом диалоге.
    const interval = window.setInterval(markSoon, 5000);
    markSoon();
    return () => {
      window.removeEventListener("focus", markSoon);
      window.removeEventListener("resize", markSoon);
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearInterval(interval);
    };
  }, [activeDialogId, markVisibleUnreadMessages]);

  React.useEffect(() => {
    autoScrollDialogRef.current = activeDialogId ? String(activeDialogId) : "";
    lastAutoScrollMessageKeyRef.current = "";
  }, [activeDialogId]);

  React.useEffect(() => {
    if (!activeDialogId) return;
    if (messagesLoading) return;
    if (autoScrollDialogRef.current !== String(activeDialogId)) return;
    autoScrollDialogRef.current = "";
    const raf = window.requestAnimationFrame(() => scrollToBottom(false));
    return () => window.cancelAnimationFrame(raf);
  }, [activeDialogId, messagesLoading, scrollToBottom]);

  React.useEffect(() => {
    if (!activeDialogId) return;
    if (!orderedMessages.length) return;
    if (loadingMoreRef.current) return;

    const lastMessage = orderedMessages[orderedMessages.length - 1];
    const lastKey = `${String(activeDialogId)}:${messageDomKey(lastMessage)}`;
    if (lastAutoScrollMessageKeyRef.current === lastKey) return;
    lastAutoScrollMessageKeyRef.current = lastKey;

    const mine = isMineMessage(lastMessage, myId);
    const container = messagesRef.current;
    const distanceToBottom = container
      ? container.scrollHeight - container.scrollTop - container.clientHeight
      : 0;
    const nearBottom = distanceToBottom < 220;

    if (mine || nearBottom || !showScrollBottomFab) {
      scrollToBottom(mine);
      if (!mine) {
        window.setTimeout(() => markVisibleUnreadMessages(), 80);
        window.setTimeout(() => markVisibleUnreadMessages(), 260);
      }
    }
  }, [activeDialogId, myId, orderedMessages, scrollToBottom, showScrollBottomFab, loadingMoreRef, messagesRef, markVisibleUnreadMessages]);

  React.useEffect(() => {
    if (!activeDialogId || !orderedMessages.length) return undefined;
    const lastMessage = orderedMessages[orderedMessages.length - 1];
    if (!lastMessage || isMineMessage(lastMessage, myId)) return undefined;
    if (isReadByMe(lastMessage, isPsychologistLike)) return undefined;

    const timers = [
      window.setTimeout(() => markVisibleUnreadMessages(), 120),
      window.setTimeout(() => markVisibleUnreadMessages(), 420),
      window.setTimeout(() => markVisibleUnreadMessages(), 900),
    ];

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [activeDialogId, isPsychologistLike, markVisibleUnreadMessages, myId, orderedMessages]);

  React.useEffect(() => {
    closeMessageReaders();
  }, [activeDialogId, closeMessageReaders]);

  React.useEffect(() => {
    if (!messageReadersState.messageId) return;
    const exists = orderedMessages.some(
      (message) => String(message?.id ?? "") === String(messageReadersState.messageId)
    );
    if (!exists) closeMessageReaders();
  }, [closeMessageReaders, messageReadersState.messageId, orderedMessages]);

  // Close picker on outside click
  React.useEffect(() => {
    if (!emojiPickerOpen) return;

    const handleClick = (e) => {
      const emojiPicker = document.querySelector(".emoji-picker");
      const emojiBtn = document.querySelector(".input-emoji-btn");
      if (
        emojiPicker &&
        !emojiPicker.contains(e.target) &&
        emojiBtn &&
        !emojiBtn.contains(e.target)
      ) {
        setEmojiPickerOpen(false);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [emojiPickerOpen, setEmojiPickerOpen]);

  React.useEffect(() => {
    if (!messageReadersState.messageId) return;

    const handlePointerDown = (event) => {
      if (event.target instanceof Element && event.target.closest(".msg-status-wrap")) return;
      closeMessageReaders();
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") closeMessageReaders();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [closeMessageReaders, messageReadersState.messageId]);

  const onMessagesScroll = React.useCallback(
    async (event) => {
      const element = event.currentTarget;
      const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      setShowScrollBottomFab(distanceToBottom > 160);
      markVisibleUnreadMessages();

      if (element.scrollTop > 80) return;
      if (loadingMoreRef.current) return;
      if (messagesLoading) return;

      loadingMoreRef.current = true;
      setLoadingMore(true);

      const prevHeight = element.scrollHeight;
      const prevTop = element.scrollTop;

      try {
        await Promise.resolve(loadMore());
      } finally {
        window.requestAnimationFrame(() => {
          const nextHeight = element.scrollHeight;
          element.scrollTop = Math.max(0, nextHeight - prevHeight + prevTop);
          loadingMoreRef.current = false;
          setLoadingMore(false);
        });
      }
    },
    [loadMore, loadingMoreRef, markVisibleUnreadMessages, messagesLoading, setLoadingMore, setShowScrollBottomFab]
  );

  const onAttachFiles = React.useCallback(() => {
    if (!chatCanSendMedia) {
      toast.info(chatMediaRestrictionText);
      return;
    }
    fileInputRef.current?.click();
  }, [chatCanSendMedia, chatMediaRestrictionText, fileInputRef, toast]);

  const onPickFiles = React.useCallback(
    (event) => {
      if (!chatCanSendMedia) {
        event.target.value = "";
        toast.info(chatMediaRestrictionText);
        return;
      }
      const picked = Array.from(event.target.files || []);
      event.target.value = "";
      if (!picked.length) return;

      let rejectedBySize = 0;
      let rejectedByType = 0;
      let rejectedByLimit = 0;
      let skippedDuplicates = 0;

      setPendingFiles((prev) => {
        const next = [...prev];
        const seen = new Set(next.map(fileFingerprint));
        for (const file of picked) {
          if (!(file instanceof File)) continue;
          if (next.length >= CHAT_MAX_FILES_PER_MESSAGE) {
            rejectedByLimit += 1;
            continue;
          }

          const fp = fileFingerprint(file);
          if (seen.has(fp)) {
            skippedDuplicates += 1;
            continue;
          }

          if (Number(file.size || 0) > CHAT_MAX_FILE_SIZE_BYTES) {
            rejectedBySize += 1;
            continue;
          }

          if (isBlockedFile(file)) {
            rejectedByType += 1;
            continue;
          }

          seen.add(fp);
          next.push(file);

          // Generate preview for images
          if (isImageLikeFileValue(file)) {
            const reader = new FileReader();
            reader.onload = (e) => {
              setPendingFilePreviews((prev) => ({
                ...prev,
                [fp]: e.target?.result,
              }));
            };
            reader.readAsDataURL(file);
          }
        }

        return next;
      });

      if (rejectedByLimit > 0) {
        toast.info(`Можно прикрепить не более ${CHAT_MAX_FILES_PER_MESSAGE} файлов`);
      }
      if (rejectedBySize > 0) {
        toast.error(`Некоторые файлы больше ${(CHAT_MAX_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(0)} МБ`);
      }
      if (rejectedByType > 0) {
        toast.error("Некоторые файлы отклонены из соображений безопасности");
      }
      if (skippedDuplicates > 0) {
        toast.info("Повторяющиеся файлы пропущены");
      }
    },
    [chatCanSendMedia, chatMediaRestrictionText, setPendingFilePreviews, setPendingFiles, toast]
  );

  const onRemovePendingFile = React.useCallback((index) => {
    setPendingFiles((prev) => {
      const file = prev[index];
      const fp = file ? fileFingerprint(file) : null;
      if (fp) {
        setPendingFilePreviews((prev) => {
          const next = { ...prev };
          delete next[fp];
          return next;
        });
      }
      return prev.filter((_, idx) => idx !== index);
    });
  }, [setPendingFilePreviews, setPendingFiles]);

  const triggerReactionBadgeEffect = React.useCallback((messageId, emoji, fallbackElement = null) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const row = document.querySelector(`[data-message-id="${String(messageId)}"]`);
        const badge = row
          ? Array.from(row.querySelectorAll("[data-reaction-emoji]")).find((node) => node.dataset.reactionEmoji === String(emoji))
          : null;
        const bubble = row?.querySelector?.(".msg-bubble");
        const target = badge || fallbackElement || bubble;
        if (target?.classList) {
          target.classList.remove("is-effecting");
          // force reflow for repeated reactions on the same badge
          void target.offsetWidth;
          target.classList.add("is-effecting");
          window.setTimeout(() => target.classList?.remove("is-effecting"), 1050);
        }
        window.setTimeout(() => createParticles(target, emoji), 140);
      });
    });
  }, []);

  React.useEffect(() => {
    const handleIncomingReactionEffect = (event) => {
      const detail = event?.detail || {};
      if (!detail.messageId || !detail.emoji) return;
      if (detail.dialogId != null && activeDialogId != null && String(detail.dialogId) !== String(activeDialogId)) return;
      triggerReactionBadgeEffect(detail.messageId, detail.emoji);
    };

    window.addEventListener("chat:reaction-effect", handleIncomingReactionEffect);
    return () => window.removeEventListener("chat:reaction-effect", handleIncomingReactionEffect);
  }, [activeDialogId, triggerReactionBadgeEffect]);

  const onToggleReaction = React.useCallback(
    async (message, emoji, forceAdd = null, event = null) => {
      const messageId = message?.id;
      if (!activeDialogId || !isServerMessageId(messageId)) return;

      const existing = (Array.isArray(message?.reactions) ? message.reactions : []).find(
        (reaction) => reaction.emoji === emoji
      );

      const add = forceAdd == null ? !Boolean(existing?.me) : Boolean(forceAdd);
      const fallbackElement = event?.currentTarget || null;

      const result = await reactToMessage({
        dialogId: activeDialogId,
        messageId,
        emoji,
        add,
      });

      if (add && result !== null) {
        triggerReactionBadgeEffect(messageId, emoji, fallbackElement);
      }
    },
    [activeDialogId, reactToMessage, triggerReactionBadgeEffect]
  );

  const onRetryFailed = React.useCallback(
    async (message) => {
      if (!activeDialogId) return;
      const text = String(message?.text || "").trim();
      if (!text) return;
      try {
        await sendMessage({ dialogId: activeDialogId, text, files: [] });
      } catch (error) {
        toast.error(error?.message || "Не удалось повторить отправку");
      }
    },
    [activeDialogId, sendMessage, toast]
  );

  const onDeleteMessage = React.useCallback(
    async (message) => {
      if (!activeDialogId || !message?.id) return;
      if (!isServerMessageId(message.id)) return;

      setConfirmDeleteMessage(message);
    },
    [activeDialogId, setConfirmDeleteMessage]
  );

  const onVerificationAction = React.useCallback(
    async (message, decision) => {
      const parsed = extractPsychologistVerificationAction(message);
      const userId = toUserId(parsed?.userId);
      const messageKey = String(message?.id ?? message?.clientId ?? "");
      if (!userId || !messageKey) return;
      if (decision !== "approve" && decision !== "reject") return;

      setVerificationActionStateByMessage((prev) => ({
        ...prev,
        [messageKey]: {
          ...(prev[messageKey] || {}),
          userId,
          pending: true,
          error: "",
        },
      }));

      try {
        let response = null;
        if (decision === "approve") {
          response = await psychologistVerificationApi.approve(userId, "Одобрено из action-чата");
        } else {
          response = await psychologistVerificationApi.reject(userId, "Отклонено из action-чата");
        }
        const item = pickVerificationItem(response);
        const resolvedFromApi = normalizeVerificationResolution(
          item?.verificationStatus || item?.psychologistVerificationStatus || item?.status || ""
        );
        const resolvedDecision = resolvedFromApi || decision;
        const reviewerNameFromApi = String(
          item?.reviewedByUserName ||
            item?.reviewerName ||
            item?.verifiedByName ||
            ""
        ).trim();
        const fallbackReviewerName = String(me?.name || me?.login || me?.email || "").trim();
        const reviewerName = reviewerNameFromApi || fallbackReviewerName;
        const reviewerUserId = toUserId(
          item?.reviewedByUserId ?? item?.reviewerUserId ?? item?.verifiedByUserId ?? myId
        );
        setVerificationStatusByUserId((prev) => ({
          ...mergeVerificationStatusCacheEntry(prev, userId, {
            status: resolvedDecision === "approve" ? "APPROVED" : "REJECTED",
            reviewerName,
            reviewerUserId,
            fetchedAt: Date.now(),
          }),
        }));
        setVerificationActionStateByMessage((prev) => ({
          ...prev,
          [messageKey]: {
            userId,
            pending: false,
            error: "",
            resolved: resolvedDecision,
            reviewerName,
            reviewerUserId,
          },
        }));
        toast.success(decision === "approve" ? "Психолог верифицирован" : "Заявка отклонена");
      } catch (error) {
        setVerificationActionStateByMessage((prev) => ({
          ...prev,
          [messageKey]: {
            ...(prev[messageKey] || {}),
            userId,
            pending: false,
            error: error?.message || "Не удалось выполнить действие",
          },
        }));
        toast.error(error?.message || "Не удалось выполнить действие");
      }
    },
    [me?.email, me?.login, me?.name, myId, toast]
  );

  const onConfirmDelete = React.useCallback(
    async () => {
      if (!confirmDeleteMessage || !activeDialogId) return;

      await deleteMessage({ dialogId: activeDialogId, messageId: confirmDeleteMessage.id });
      setConfirmDeleteMessage(null);
    },
    [activeDialogId, confirmDeleteMessage, deleteMessage, setConfirmDeleteMessage]
  );

  // Start editing a message. Existing media stays editable: user can keep/remove it
  // and can attach new files in the same composer, like when creating a message.
  const startEditMessage = React.useCallback((message) => {
    if (!isMineMessage(message, myId)) return;
    if (!isServerMessageId(message?.id)) return;
    if (isMessageDeleted(message)) return;

    const existingAttachments = Array.isArray(message?.attachments) ? message.attachments.filter(Boolean) : [];
    setEditingMessage(message);
    setEditingExistingAttachments(existingAttachments);
    setDraft(String(message?.text || ""));
    setReplyTo(null);
    setPendingFiles([]);
    setPendingFilePreviews({});
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  }, [myId, setDraft, setEditingMessage, setPendingFilePreviews, setPendingFiles, setReplyTo, textareaRef]);

  // Cancel editing
  const cancelEditMessage = React.useCallback(() => {
    setEditingMessage(null);
    setEditingExistingAttachments([]);
    setDraft("");
    setPendingFiles([]);
    setPendingFilePreviews({});
  }, [setDraft, setEditingMessage, setPendingFilePreviews, setPendingFiles]);

  const removeEditingExistingAttachment = React.useCallback((attachment) => {
    const key = mediaAttachmentKey(attachment);
    setEditingExistingAttachments((prev) => prev.filter((item) => mediaAttachmentKey(item) !== key));
  }, [mediaAttachmentKey]);

  // Save the edited message
  const saveEditMessage = React.useCallback(async () => {
    if (!editingMessage || !activeDialogId) return;

    const normalizedDraft = draft.slice(0, CHAT_MAX_MESSAGE_LENGTH);
    const text = normalizedDraft.trim();
    const originalAttachments = Array.isArray(editingMessage?.attachments) ? editingMessage.attachments : [];
    const filesToSend = pendingFiles.slice(0, CHAT_MAX_FILES_PER_MESSAGE);
    const keepAttachmentIds = editingExistingAttachments
      .map((attachment) => attachment?.id ?? attachment?.attachmentId ?? attachment?.url ?? attachment?.name)
      .filter(Boolean)
      .map(String);
    const keepSet = new Set(keepAttachmentIds);
    const removeAttachmentIds = originalAttachments
      .map((attachment) => attachment?.id ?? attachment?.attachmentId ?? attachment?.url ?? attachment?.name)
      .filter(Boolean)
      .map(String)
      .filter((id) => !keepSet.has(id));

    if (!text && !editingExistingAttachments.length && !filesToSend.length) return;
    if (filesToSend.length && !chatCanSendMedia) {
      toast.info(chatMediaRestrictionText);
      return;
    }

    try {
      await editMessage({
        dialogId: activeDialogId,
        messageId: editingMessage.id,
        text,
        files: filesToSend,
        keepAttachmentIds,
        removeAttachmentIds,
      });
      setEditingMessage(null);
      setEditingExistingAttachments([]);
      setDraft("");
      setPendingFiles([]);
      setPendingFilePreviews({});
    } catch (error) {
      toast.error(error?.message || "Не удалось изменить сообщение");
    }
  }, [
    activeDialogId,
    chatCanSendMedia,
    chatMediaRestrictionText,
    draft,
    editingExistingAttachments,
    editingMessage,
    editMessage,
    pendingFiles,
    setDraft,
    setEditingMessage,
    setPendingFilePreviews,
    setPendingFiles,
    toast,
  ]);

  React.useEffect(() => {
    setReplyTo(null);
    setDraft("");
    setPendingFiles([]);
    setPendingFilePreviews({});
    setEditingMessage(null);
    setEditingExistingAttachments([]);
    setShowScrollBottomFab(false);
  }, [
    activeDialogId,
    setDraft,
    setEditingMessage,
    setPendingFilePreviews,
    setPendingFiles,
    setReplyTo,
    setShowScrollBottomFab,
  ]);

  const onSend = React.useCallback(async () => {
    if (!activeDialogId || !activeDialog || activeDialog.locked) return;

    // Editing own message should be available even if the host later limited new messages.
    if (editingMessage) {
      await saveEditMessage();
      return;
    }

    if (!chatCanWrite) {
      toast.info(chatWriteRestrictionText || "Отправка сообщений ограничена");
      return;
    }

    const normalizedDraft = draft.slice(0, CHAT_MAX_MESSAGE_LENGTH);
    const text = normalizedDraft.trim();
    const filesToSend = pendingFiles.slice(0, CHAT_MAX_FILES_PER_MESSAGE);
    const hasFiles = filesToSend.length > 0;
    if (!text && !hasFiles) return;
    if (hasFiles && !chatCanSendMedia) {
      toast.info(chatMediaRestrictionText);
      return;
    }

    const payload = {
      dialogId: activeDialogId,
      text,
      files: filesToSend,
      replyToMessageId: replyTo?.messageId || replyTo?.id || null,
      replyToClientId: replyTo?.clientId || null,
      replyToKey: replyTo?.key || replyTo?.messageId || replyTo?.id || null,
      replyTo,
    };

    setDraft("");
    setPendingFiles([]);
    setPendingFilePreviews({});
    setReplyTo(null);

    if (!hasFiles) {
      void sendMessage(payload);
      return;
    }

    setSending(true);
    try {
      await sendMessage(payload);
    } catch (error) {
      toast.error(error?.message || "Не удалось отправить сообщение");
    } finally {
      setSending(false);
    }
  }, [
    activeDialog,
    activeDialogId,
    chatCanSendMedia,
    chatCanWrite,
    chatMediaRestrictionText,
    chatWriteRestrictionText,
    draft,
    editingMessage,
    pendingFiles,
    replyTo,
    saveEditMessage,
    sendMessage,
    setDraft,
    setPendingFilePreviews,
    setPendingFiles,
    setReplyTo,
    setSending,
    toast,
  ]);

  const onDraftKeyDown = React.useCallback(
    (event) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        onSend();
        return;
      }

      if (activeDialogId) {
        notifyTyping(activeDialogId);
      }
    },
    [activeDialogId, notifyTyping, onSend]
  );

  const onDraftChange = React.useCallback(
    (event) => {
      const raw = String(event.target.value || "");
      if (raw.length > CHAT_MAX_MESSAGE_LENGTH) {
        const now = Date.now();
        if (now - draftLimitNoticeAtRef.current > 2500) {
          draftLimitNoticeAtRef.current = now;
          toast.info(`Максимум ${CHAT_MAX_MESSAGE_LENGTH} символов в сообщении`);
        }
      }
      setDraft(raw.slice(0, CHAT_MAX_MESSAGE_LENGTH));
      if (activeDialogId) {
        notifyTyping(activeDialogId);
      }
    },
    [activeDialogId, draftLimitNoticeAtRef, notifyTyping, setDraft, toast]
  );

  const getDialogAvatarUrl = React.useCallback(
    (dialog) => {
      const dialogId = dialog?.id == null ? "" : String(dialog.id);
      const titleOverride = dialogId ? titleOverridesByDialog[dialogId] : "";
      const fallback = isAegisDialogCandidate(dialog, titleOverride) ? AEGIS_AVATAR_URL : "";
      return (
        (dialogId ? avatarOverridesByDialog[dialogId] : "") ||
        dialog?.avatarUrl ||
        dialog?.avatar ||
        dialog?.imageUrl ||
        dialog?.photoUrl ||
        fallback
      );
    },
    [avatarOverridesByDialog, titleOverridesByDialog]
  );

  const renderDialogAvatar = React.useCallback((dialog) => {
    const title = getDialogTitle(dialog);
    const avatarUrl = getDialogAvatarUrl(dialog);
    const safeUrl = safeExternalUrl(avatarUrl) || avatarUrl;

    if (avatarUrl) {
      return (
        <div className={`dialog-avatar ${isGroupDialog(dialog) ? "group-av" : ""} ${dialog?.online ? "online" : ""}`}>
          <img src={safeUrl} alt="" />
        </div>
      );
    }

    if (isGroupDialog(dialog)) {
      return (
        <div className="dialog-avatar group-av" style={{ background: "var(--lav-soft)", color: "#7B6FD4" }}>
          <Icon size={18}>
            <circle cx="9" cy="7" r="4" />
            <path d="M2 21v-2a4 4 0 014-4h6a4 4 0 014 4v2" />
            <circle cx="19" cy="8" r="3" />
          </Icon>
        </div>
      );
    }

    return (
      <div className={`dialog-avatar ${dialog?.online ? "online" : ""}`} style={{ background: "var(--mint)" }}>
        {initials(title)}
      </div>
    );
  }, [getDialogAvatarUrl]);

  const renderMessageAvatar = React.useCallback(
    (message, mine) => {
      if (mine) return <div className="msg-avatar" aria-hidden="true" />;

      const senderMember = activeDialogMembers.find((member) => String(member?.userId ?? member?.id ?? "") === String(message?.fromUserId ?? ""));
      const senderName =
        message?.fromName ||
        message?.fromUserName ||
        senderMember?.name ||
        senderMember?.fullName ||
        activeDialogTitle;
      const useAegisAvatar = isAegisLabel(senderName) || isAegisLabel(message?.systemType) || isAegisLabel(message?.system_type);
      const avatarUrl =
        senderMember?.avatarUrl ||
        senderMember?.avatar ||
        senderMember?.photoUrl ||
        message?.fromAvatarUrl ||
        message?.avatarUrl ||
        (useAegisAvatar ? AEGIS_AVATAR_URL : "");
      const safeUrl = safeExternalUrl(avatarUrl) || avatarUrl;

      return (
        <div className="msg-avatar" style={avatarUrl ? undefined : { background: "var(--mint)" }}>
          {avatarUrl ? <img src={safeUrl} alt="" /> : initials(senderName)}
        </div>
      );
    },
    [activeDialogMembers, activeDialogTitle]
  );

  const renderReplyBlock = React.useCallback(
    (message) => {
      if (!message?.replyTo) return null;

      const reply = message.replyTo;
      const key = reply.messageId || reply.id || reply.clientId || null;
      const name = reply.fromName || "Сообщение";
      const text = String(reply.text || "").trim() || "Вложение";

      return (
        <button
          type="button"
          className="msg-reply-block"
          onClick={() => {
            if (key) jumpToMessage(key);
          }}
        >
          <div className="msg-reply-name">{name}</div>
          <div className="msg-reply-text">{text}</div>
        </button>
      );
    },
    [jumpToMessage]
  );

  const renderMessageAttachments = React.useCallback(
    (message) => {
      const attachments = Array.isArray(message?.attachments) ? message.attachments : [];
      if (!attachments.length) return null;

      return attachments.map((attachment) => {
        const kind = isImageLikeFileValue(attachment) ? "image" : (attachment?.kind || "file");
        const imageUrl = safeExternalUrl(attachment?.url, { allowDataImage: true });
        const fileUrl = safeExternalUrl(attachment?.url);

        if (kind === "image" && imageUrl) {
          return (
            <button
              type="button"
              className="msg-image-wrap msg-image-wrap--button"
              key={mediaAttachmentKey(attachment) || imageUrl}
              title="Открыть изображение"
              onClick={(event) => {
                event.stopPropagation();
                openMediaAtAttachment(attachment);
              }}
            >
              <img loading="lazy" src={imageUrl} alt={attachment.name || "Изображение"} />
            </button>
          );
        }

        return (
          <a
            className="msg-file"
            key={attachment.id || fileUrl || attachment.name}
            href={fileUrl || "#"}
            target={fileUrl ? "_blank" : undefined}
            rel={fileUrl ? "noreferrer noopener" : undefined}
            onClick={(event) => {
              if (!fileUrl) event.preventDefault();
            }}
          >
            <div className="msg-file-icon">
              <Icon size={16}>
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </Icon>
            </div>
            <div className="msg-file-info">
              <div className="msg-file-name">{attachment.name || "Файл"}</div>
              <div className="msg-file-size">{bytesLabel(attachment.size)}</div>
            </div>
          </a>
        );
      });
    },
    [mediaAttachmentKey, openMediaAtAttachment]
  );

  const renderMessageMeta = React.useCallback(
    (message, mine) => {
      const failed = message?.status === "failed";
      const pending = message?.status === "pending";
      const read = isReadByOther(message, isPsychologistLike);
      const canOpenReaders = mine && !pending && isServerMessageId(message?.id);
      const isReadersOpen = canOpenReaders && String(messageReadersState.messageId ?? "") === String(message?.id ?? "");

      return (
        <div className="msg-meta">
          {isMessageEdited(message) ? <span className="msg-edited">ред.</span> : null}
          <span className="msg-time">{formatMessageTime(message?.createdAt)}</span>
          {mine ? (
            <span className="msg-status-wrap">
              {canOpenReaders ? (
                <button
                  type="button"
                  className={`msg-status ${read ? "read" : "sent"} is-clickable`}
                  aria-label="Статус прочтения"
                  aria-expanded={isReadersOpen}
                  onClick={(event) => onMessageStatusClick(message, event)}
                >
                  <CheckIcon read={read} />
                </button>
              ) : (
                <span className={`msg-status ${read ? "read" : "sent"}`}>
                  {pending ? "…" : <CheckIcon read={read} />}
                </span>
              )}

              {isReadersOpen ? (
                <div
                  className="chat-readers-popover"
                  role="dialog"
                  aria-label="Кто прочитал сообщение"
                  onClick={(event) => event.stopPropagation()}
                >
                  {messageReadersState.loading ? (
                    <div className="chat-readers-popover__state">Загружаем...</div>
                  ) : messageReadersState.error ? (
                    <div className="chat-readers-popover__state is-error">{messageReadersState.error}</div>
                  ) : messageReadersState.readers.length ? (
                    <div className="chat-readers-popover__list">
                      {messageReadersState.readers.map((reader, index) => (
                        <div className="chat-readers-popover__item" key={`${reader.userId || "reader"}-${index}`}>
                          <div className="chat-readers-popover__name">{reader.name}</div>
                          <div className="chat-readers-popover__time">{formatReaderReadAt(reader.readAt)}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="chat-readers-popover__state">Пока никто не прочитал</div>
                  )}
                </div>
              ) : null}
            </span>
          ) : null}
          {failed ? <span className="msg-status sent">!</span> : null}
        </div>
      );
    },
    [isPsychologistLike, messageReadersState, onMessageStatusClick]
  );

  const handleSaveChatSettings = React.useCallback(async () => {
    if (!activeDialogId || !canManageChat) return;

    const title = String(chatSettingsDraft.title || "").trim();
    const payload = {
      title,
      writeMode: chatSettingsDraft.writeMode,
      mediaMode: chatSettingsDraft.mediaMode,
      allowedUserIds: chatSettingsDraft.allowedUserIds,
    };

    setChatSettingsSaving(true);
    let savedOnBackend = false;
    try {
      if (title && title !== activeDialogBaseTitle && typeof chatApi.renameDialog === "function") {
        await chatApi.renameDialog(activeDialogId, title);
      }
      if (typeof chatApi.updateDialogSettings === "function") {
        await chatApi.updateDialogSettings(activeDialogId, payload);
      }
      if (chatSettingsDraft.avatarFile && typeof chatApi.updateDialogAvatar === "function") {
        const avatarRes = await chatApi.updateDialogAvatar(activeDialogId, chatSettingsDraft.avatarFile);
        const nextAvatar = avatarRes?.avatarUrl || avatarRes?.avatar_url || avatarRes?.url || chatSettingsDraft.avatarPreview || "";
        if (nextAvatar) {
          setAvatarOverridesByDialog((prev) => ({ ...prev, [String(activeDialogId)]: nextAvatar }));
        }
      }
      savedOnBackend = true;
    } catch (error) {
      const status = Number(error?.status ?? error?.response?.status ?? 0);
      if (![404, 405, 501].includes(status)) {
        toast.error(error?.message || "Не удалось сохранить настройки чата");
        setChatSettingsSaving(false);
        return;
      }
    }

    setTitleOverridesByDialog((prev) => ({ ...prev, [String(activeDialogId)]: title || activeDialogTitle }));
    if (chatSettingsDraft.avatarPreview) {
      setAvatarOverridesByDialog((prev) => ({ ...prev, [String(activeDialogId)]: chatSettingsDraft.avatarPreview }));
    }
    setChatSettingsOverrides((prev) => ({
      ...prev,
      [String(activeDialogId)]: {
        writeMode: payload.writeMode,
        mediaMode: payload.mediaMode,
        allowedUserIds: payload.allowedUserIds,
      },
    }));

    setChatSettingsOpen(false);
    setChatSettingsSaving(false);
    if (savedOnBackend) {
      toast.success("Настройки чата сохранены");
      refreshDialogDetails?.(activeDialogId, { force: true });
    } else {
      toast.info("Настройки применены локально. Для постоянного хранения нужен backend endpoint настроек чата.");
    }
  }, [
    activeDialogBaseTitle,
    activeDialogId,
    activeDialogTitle,
    canManageChat,
    chatSettingsDraft,
    refreshDialogDetails,
    toast,
  ]);

  const activeDialogNotificationsMuted = React.useMemo(() => {
    if (!activeDialogId) return false;
    const local = chatNotificationMutedByDialog[String(activeDialogId)];
    if (local != null) return Boolean(local);
    return Boolean(activeDialog?.notificationsMuted ?? activeDialog?.muted ?? activeDialog?.muteNotifications);
  }, [activeDialog, activeDialogId, chatNotificationMutedByDialog]);

  const toggleChatNotifications = React.useCallback(async () => {
    if (!activeDialogId) return;
    const nextMuted = !activeDialogNotificationsMuted;
    setChatNotificationMutedByDialog((prev) => ({ ...prev, [String(activeDialogId)]: nextMuted }));
    try {
      if (typeof chatApi.updateDialogNotifications === "function") {
        await chatApi.updateDialogNotifications(activeDialogId, { muted: nextMuted, notificationsMuted: nextMuted });
        toast.success(nextMuted ? "Уведомления чата выключены" : "Уведомления чата включены");
        refreshDialogDetails?.(activeDialogId, { force: true });
      } else {
        toast.info("Настройка уведомлений применена локально. Для постоянного хранения нужен backend endpoint.");
      }
    } catch (error) {
      const status = Number(error?.status ?? error?.response?.status ?? 0);
      if ([404, 405, 501].includes(status)) {
        toast.info("Настройка уведомлений применена локально. Backend endpoint пока не отвечает.");
      } else {
        setChatNotificationMutedByDialog((prev) => ({ ...prev, [String(activeDialogId)]: !nextMuted }));
        toast.error(error?.message || "Не удалось изменить уведомления чата");
      }
    }
  }, [activeDialogId, activeDialogNotificationsMuted, refreshDialogDetails, toast]);

  const handleToggleAllowedWriter = React.useCallback((memberId) => {
    const id = toUserId(memberId);
    if (!id) return;
    setChatSettingsDraft((prev) => {
      const exists = prev.allowedUserIds.some((item) => Number(item) === Number(id));
      return {
        ...prev,
        allowedUserIds: exists
          ? prev.allowedUserIds.filter((item) => Number(item) !== Number(id))
          : [...prev.allowedUserIds, String(id)],
      };
    });
  }, []);

  const pinMessage = React.useCallback(
    async (message, scope = "self") => {
      if (!activeDialogId || !message?.id || !isServerMessageId(message.id)) return;
      const key = `${message.id}:${scope}`;
      setPinSavingKey(key);
      const pin = {
        messageId: String(message.id),
        scope,
        text: buildMessagePreview(message),
        pinnedAt: new Date().toISOString(),
      };
      try {
        if (typeof chatApi.pinMessage === "function") {
          await chatApi.pinMessage(activeDialogId, message.id, { scope });
          toast.success(scope === "global" ? "Сообщение закреплено для всех" : "Сообщение закреплено для вас");
          refreshDialogDetails?.(activeDialogId, { force: true });
        } else {
          toast.info("Закрепление применено локально. Для постоянного хранения нужен backend endpoint pin.");
        }
        setPinnedByDialog((prev) => ({ ...prev, [String(activeDialogId)]: pin }));
      } catch (error) {
        const status = Number(error?.status ?? error?.response?.status ?? 0);
        if ([404, 405, 501].includes(status)) {
          setPinnedByDialog((prev) => ({ ...prev, [String(activeDialogId)]: pin }));
          toast.info("Закрепление применено локально. Backend endpoint pin пока не отвечает.");
        } else {
          toast.error(error?.message || "Не удалось закрепить сообщение");
        }
      } finally {
        setPinSavingKey("");
      }
    },
    [activeDialogId, refreshDialogDetails, toast]
  );

  const unpinMessage = React.useCallback(async () => {
    if (!activeDialogId || !activePinnedMessage?.messageId) return;
    const current = activePinnedMessage;
    setPinnedByDialog((prev) => {
      const next = { ...prev };
      delete next[String(activeDialogId)];
      return next;
    });
    try {
      if (typeof chatApi.unpinMessage === "function") {
        await chatApi.unpinMessage(activeDialogId, current.messageId, { scope: current.scope });
        refreshDialogDetails?.(activeDialogId, { force: true });
      }
    } catch {
      // Local unpin is still useful if backend method is absent.
    }
  }, [activeDialogId, activePinnedMessage, refreshDialogDetails]);

  const showEmptyState = !activeDialog;
  const canSendMessage =
    Boolean(activeDialogId) &&
    !activeDialog?.locked &&
    (chatCanWrite || Boolean(editingMessage)) &&
    (pendingFiles.length === 0 || chatCanSendMedia) &&
    (
      Boolean(draft.trim()) ||
      pendingFiles.length > 0 ||
      (Boolean(editingMessage) && editingExistingAttachments.length > 0)
    ) &&
    !sending;

  return (
    <div className={`b-chat-v2 ${embedded ? "is-embedded" : ""}`.trim()}>
      {!embedded ? (
        <aside className="crm-nav">
        <div className="crm-nav-logo" />

        <div className="crm-nav-item" title="Клиенты">
          <Icon>
            <circle cx="9" cy="7" r="4" />
            <path d="M2 21v-2a4 4 0 014-4h6a4 4 0 014 4v2" />
            <circle cx="19" cy="8" r="3" />
            <path d="M22 21v-1.5a3 3 0 00-2.5-2.96" />
          </Icon>
        </div>
        <div className="crm-nav-item" title="Календарь">
          <Icon>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </Icon>
        </div>
        <div className="crm-nav-item active" title="Чаты">
          <Icon>
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </Icon>
          <div className="crm-nav-badge" />
        </div>
        <div className="crm-nav-item" title="Видеосессии">
          <Icon>
            <rect x="2" y="6" width="14" height="12" rx="2" />
            <path d="M16 9.5l6-3.5v12l-6-3.5" />
          </Icon>
        </div>
        <div className="crm-nav-item" title="Аналитика">
          <Icon>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M8 15v-4M12 15v-6M16 15v-2" />
          </Icon>
        </div>

        <div className="crm-nav-spacer" />

        <div className="crm-nav-item" title="Уведомления">
          <Icon>
            <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 01-3.46 0" />
          </Icon>
        </div>
        <div className="crm-nav-item" title="Настройки">
          <Icon>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
          </Icon>
        </div>

        <div className="crm-nav-avatar">{initials(me?.name || me?.email || "Вы")}</div>
        </aside>
      ) : null}

      <div className={`chat-app ${embedded ? "chat-app--embedded" : ""} ${singleDialogMode ? "chat-app--single-dialog" : ""}`.trim()}>
        {!singleDialogMode ? (
        <div className={`chat-sidebar ${sidebarHiddenMobile ? "hidden-mobile" : ""}`} id="chatSidebar">
          <div className="sidebar-header">
            <div className="sidebar-title-row">
              <div className="sidebar-title">Чаты</div>
            </div>

            <div className="sidebar-search">
              <Icon className="sidebar-search-icon" size={14}>
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </Icon>
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Поиск по чатам..."
              />
            </div>
          </div>

          <div className="sidebar-filters">
            <button type="button" className={`filter-chip ${filter === FILTER_ALL ? "active" : ""}`} onClick={() => setFilter(FILTER_ALL)}>
              Все
            </button>
            <button type="button" className={`filter-chip ${filter === FILTER_CLIENTS ? "active" : ""}`} onClick={() => setFilter(FILTER_CLIENTS)}>
              Клиенты
            </button>
            <button type="button" className={`filter-chip ${filter === FILTER_GROUPS ? "active" : ""}`} onClick={() => setFilter(FILTER_GROUPS)}>
              Групповые
            </button>
            <button type="button" className={`filter-chip ${filter === FILTER_ARCHIVE ? "active" : ""}`} onClick={() => setFilter(FILTER_ARCHIVE)}>
              Архив
            </button>
          </div>

          <div className="dialog-list">
            {dialogsLoading ? <div className="dialog-list-state">Загружаем диалоги...</div> : null}
            {!dialogsLoading && dialogsError ? <div className="dialog-list-state error">{dialogsError}</div> : null}

            {!dialogsLoading && !dialogsError && dialogsFiltered.length === 0 ? (
              <div className="dialog-list-empty" role="status" aria-live="polite">
                <div className="dialog-list-empty__icon" aria-hidden="true">
                  <Icon size={18}>
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </Icon>
                </div>
                <div className="dialog-list-empty__title">
                  {search.trim() ? "Ничего не найдено" : "Пока нет диалогов"}
                </div>
                <div className="dialog-list-empty__sub">
                  {search.trim()
                    ? "Попробуйте изменить запрос или выбрать другой фильтр."
                    : "Когда появятся новые сообщения, диалоги отобразятся здесь."}
                </div>
              </div>
            ) : null}

            {dialogsFiltered.map((dialog) => {
              const isActive = String(dialog.id) === String(activeDialogId || "");
              const isUnread = Number(dialog.unreadCount || 0) > 0;
              const preview = getDialogPreview(dialog);
              const typingPreview = buildDialogTypingPreview(dialog);
              const showTypingPreview = Boolean(typingPreview);
              const previewText = showTypingPreview ? typingPreview : preview;

              return (
                <button
                  type="button"
                  className={`dialog-item ${isActive ? "active" : ""} ${isUnread ? "unread" : ""}`}
                  key={dialog.id}
                  onClick={() => selectDialog(dialog.id)}
                >
                  {renderDialogAvatar(dialog)}
                  <div className="dialog-content">
                    <div className="dialog-top-row">
                      <div className="dialog-name">{titleOverridesByDialog[String(dialog.id)] || getDialogTitle(dialog)}</div>
                      <div className="dialog-time">{formatDialogTime(dialog.updatedAt)}</div>
                    </div>
                    <div className="dialog-bottom-row">
                      <div className="dialog-preview">
                        {showTypingPreview ? <span className="typing-text">{previewText}</span> : preview}
                      </div>
                      {isUnread ? <div className="dialog-unread-badge">{dialog.unreadCount}</div> : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        ) : null}

        <div className="chat-area" id="chatArea">
          {showEmptyState ? (
            <div className="chat-empty">
              <div className="chat-empty-icon">
                <Icon size={28}>
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                </Icon>
              </div>
              <div className="chat-empty-title">{singleDialogMode ? "Диалог конференции недоступен" : "Выберите диалог"}</div>
              <div className="chat-empty-sub">
                {singleDialogMode ? "Не удалось открыть диалог этой конференции." : "Откройте чат слева, чтобы начать переписку"}
              </div>
            </div>
          ) : (
            <>
              <div className="chat-header">
                {!singleDialogMode ? (
                <button className="mobile-back-btn sidebar-action-btn" type="button" onClick={showSidebar}>
                  <Icon size={16}>
                    <polyline points="15 18 9 12 15 6" />
                  </Icon>
                </button>
                ) : null}

                {activeDialogPsychologistRoute ? (
                  <button
                    type="button"
                    className="chat-header-avatar chat-header-avatar-btn"
                    style={activeDialogAvatarUrl ? undefined : { background: "var(--mint)" }}
                    title="Открыть профиль психолога"
                    onClick={(event) => openPsychologistProfile(event, activeDialogPsychologistTarget)}
                  >
                    {activeDialogAvatarUrl ? <img src={safeExternalUrl(activeDialogAvatarUrl) || activeDialogAvatarUrl} alt="" /> : initials(activeDialogTitle)}
                  </button>
                ) : (
                  <div className="chat-header-avatar" style={activeDialogAvatarUrl ? undefined : { background: "var(--mint)" }}>
                    {activeDialogAvatarUrl ? <img src={safeExternalUrl(activeDialogAvatarUrl) || activeDialogAvatarUrl} alt="" /> : initials(activeDialogTitle)}
                  </div>
                )}
                <div className="chat-header-info">
                  {activeDialogPsychologistRoute ? (
                    <button
                      type="button"
                      className="chat-header-name chat-header-name-btn"
                      onClick={(event) => openPsychologistProfile(event, activeDialogPsychologistTarget)}
                    >
                      {activeDialogTitle}
                    </button>
                  ) : (
                    <div className="chat-header-name">{activeDialogTitle}</div>
                  )}
                  <div className="chat-header-status">
                    {activeDialogIsGroup ? (
                      <>
                        <span className="status-dot status-dot--group" />
                        {activeDialogMembers.length || activeDialog?.membersCount || 1} участников
                      </>
                    ) : (
                      <>
                        <span className={`status-dot ${activeDialog?.online ? "" : "offline"}`} />
                        {activeDialog?.online
                          ? "В сети"
                          : activeDialog?.lastSeenAt
                          ? `Был(а) ${formatDialogTime(activeDialog.lastSeenAt)}`
                          : "Сейчас не в сети"}
                      </>
                    )}
                  </div>
                </div>

                <div className="chat-header-actions">
                  <button className="chat-header-btn" type="button" title="Видеозвонок">
                    <Icon size={16}>
                      <rect x="2" y="6" width="14" height="12" rx="2" />
                      <path d="M16 9.5l6-3.5v12l-6-3.5" />
                    </Icon>
                  </button>
                  <button
                    className={`chat-header-btn ${messageSearchOpen ? "active" : ""}`}
                    type="button"
                    title="Поиск в чате"
                    onClick={() => setMessageSearchOpen((prev) => !prev)}
                  >
                    <Icon size={16}>
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </Icon>
                  </button>
                  {canManageChat && !hideDialogSettings ? (
                    <button
                      className={`chat-header-btn ${chatSettingsOpen ? "active" : ""}`}
                      type="button"
                      title="Настройки чата"
                      onClick={() => setChatSettingsOpen(true)}
                    >
                      <Icon size={16}>
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.7 1.7 0 00.34 1.88l.06.06a2 2 0 01-2.83 2.83l-.06-.06A1.7 1.7 0 0015 19.4a1.7 1.7 0 00-1 .6l-.08.08a2 2 0 01-3.42-1.42v-.08A1.7 1.7 0 009.5 17a1.7 1.7 0 00-1.88-.34l-.08.04a2 2 0 01-2.73-2.73l.04-.08A1.7 1.7 0 004.6 12a1.7 1.7 0 00-.6-1l-.08-.08A2 2 0 015.34 7.5h.08A1.7 1.7 0 007 6.5a1.7 1.7 0 00-.34-1.88l-.06-.06A2 2 0 019.43 1.73l.06.06A1.7 1.7 0 0011 2.6a1.7 1.7 0 001-.6l.08-.08A2 2 0 0115.5 3.34v.08A1.7 1.7 0 0017 4.5a1.7 1.7 0 001.88-.34l.08-.04a2 2 0 012.73 2.73l-.04.08A1.7 1.7 0 0019.4 9c.37.2.7.47 1 .8" />
                      </Icon>
                    </button>
                  ) : null}
                  {!hideInfoPanel ? (
                  <button
                    className="chat-header-btn"
                    type="button"
                    title="Информация"
                    onClick={() => setInfoOpen((prev) => !prev)}
                  >
                    <Icon size={16}>
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </Icon>
                  </button>
                  ) : null}
                </div>
              </div>

              <ChatMessageSearchBar
                open={messageSearchOpen}
                query={messageSearchQuery}
                onQueryChange={(value) => {
                  setMessageSearchQuery(value);
                  setMessageSearchActiveIndex(0);
                }}
                resultCount={messageSearchResults.length}
                activeIndex={messageSearchActiveIndex}
                onPrev={goToPrevSearchResult}
                onNext={goToNextSearchResult}
                onClose={() => {
                  setMessageSearchOpen(false);
                  setMessageSearchQuery("");
                }}
              />

              {activePinnedMessage ? (
                <div className="chat-pinned-banner">
                  <button
                    type="button"
                    className="chat-pinned-banner__main"
                    onClick={() => jumpToMessage(activePinnedMessage.messageId)}
                    title="Перейти к закреплённому сообщению"
                  >
                    <span className="chat-pinned-banner__icon" aria-hidden="true">
                      <Icon size={14}>
                        <path d="M14 3l7 7" />
                        <path d="M5 21l6.5-6.5" />
                        <path d="M6.5 6.5l11 11" />
                        <path d="M8 5l11 11-4 1-8-8 1-4z" />
                      </Icon>
                    </span>
                    <span className="chat-pinned-banner__body">
                      <span className="chat-pinned-banner__label">Закреплено</span>
                      <span className="chat-pinned-banner__text">{activePinnedMessage.text}</span>
                    </span>
                    <span className="chat-pinned-banner__scope">
                      {activePinnedMessage.scope === "global" ? "для всех" : "для себя"}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="chat-pinned-banner__close"
                    onClick={unpinMessage}
                    title="Открепить"
                  >
                    ×
                  </button>
                </div>
              ) : null}

              <div
                className={`messages-area ${dragOver ? "drag-over" : ""}`}
                id="messagesArea"
                ref={messagesRef}
                onScroll={onMessagesScroll}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                onPaste={onPaste}
              >
                {(loadingMore || messagesLoading) && orderedMessages.length ? (
                  <div className="pagination-loader">
                    <div className="pagination-spinner" />
                  </div>
                ) : null}

                {messageRows.map((row) => {
                  if (row.type === "date") {
                    return (
                      <div className="date-separator" key={row.key}>
                        <span>{row.label}</span>
                      </div>
                    );
                  }

                  const { message, mine, grouped, firstInGroup } = row;
                  const deleted = isMessageDeleted(message);
                  const failed = message?.status === "failed";
                  const senderName =
                    message?.fromName ||
                    message?.fromUserName ||
                    activeDialogMembers.find((member) => Number(member?.userId ?? member?.id) === Number(message?.fromUserId))
                      ?.name ||
                    activeDialogTitle;

                  const domKey = messageDomKey(message);
                  const reactions = Array.isArray(message?.reactions) ? message.reactions : [];
                  const isStickerMessage = Boolean(message?.sticker?.emoji);
                  const contentCard = message?.contentCard || null;
                  const systemNotice = getSystemChatNotice(message, activeDialogTitle);
                  const messageActionsKey = String(message?.id ?? message?.clientId ?? row.key);
                  const isMobileActionsOpen = isMobile && mobileActionsMessageKey === messageActionsKey;
                  const verificationAction = canManageVerificationActions ? extractPsychologistVerificationAction(message) : null;
                  const verificationActionState = verificationAction
                    ? verificationActionStateByMessage[messageActionsKey] || null
                    : null;
                  const verificationUserId = toUserId(verificationAction?.userId);
                  const verificationStatusCode = verificationUserId
                    ? String(verificationStatusByUserId[String(verificationUserId)]?.status || "")
                    : "";
                  const verificationResolvedFromLocalAnyMessage = verificationUserId
                    ? String(verificationResolvedByUserIdFromLocalActions[String(verificationUserId)] || "")
                    : "";
                  const verificationReviewerName = String(
                    verificationActionState?.reviewerName ||
                      (verificationUserId
                        ? verificationStatusByUserId[String(verificationUserId)]?.reviewerName || ""
                        : "")
                  ).trim();
                  const verificationPending = Boolean(verificationActionState?.pending);
                  const verificationResolved = String(
                    verificationActionState?.resolved ||
                      verificationResolvedFromLocalAnyMessage ||
                      normalizeVerificationResolution(verificationStatusCode) ||
                      normalizeVerificationResolution(verificationAction?.resolved || "")
                  ).toLowerCase();
                  const rawMessageText = normalizeChatTextForDisplay(String(message?.text || ""));
                  const messageText = verificationAction
                    ? applyVerificationResolutionToText(rawMessageText, verificationResolved, verificationReviewerName)
                    : rawMessageText;

                  if (systemNotice) {
                    return (
                      <div
                        key={row.key}
                        id={`chat-msg-${domKey}`}
                        data-message-id={message?.id || ""}
                        data-client-id={message?.clientId || ""}
                        className={`chat-system-notice ${highlightMessageKey === domKey ? "is-highlight" : ""}`}
                      >
                        <span>{systemNotice}</span>
                        <time>{formatMessageTime(message?.createdAt)}</time>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={row.key}
                      id={`chat-msg-${domKey}`}
                      data-message-id={message?.id || ""}
                      data-client-id={message?.clientId || ""}
                      className={`msg-row ${mine ? "outgoing" : "incoming"} ${grouped ? "grouped" : ""} ${
                        firstInGroup ? "first-in-group" : ""
                      } ${highlightMessageKey === domKey ? "is-highlight" : ""} ${isMobileActionsOpen ? "is-mobile-actions-open" : ""}`}
                      onDoubleClick={(e) => {
                        if (e.target.closest('.msg-reactions')) return;
                        if (deleted || failed) return;
                        onToggleReaction(message, '❤️', true, { currentTarget: e.currentTarget });
                      }}
                      onContextMenu={(e) => {
                        if (deleted || failed) return;
                        e.preventDefault();
                        showReactionPicker(message, e);
                      }}
                      onClick={(event) => {
                        if (!isMobile) return;
                        if (deleted || failed) return;
                        if (
                          event.target instanceof Element &&
                          event.target.closest("button, a, input, textarea, label, .msg-reactions, .msg-file, .msg-image-wrap--button, .msg-actions")
                        ) {
                          return;
                        }
                        setMobileActionsMessageKey((prev) => (prev === messageActionsKey ? "" : messageActionsKey));
                      }}
                    >
                      {renderMessageAvatar(message, mine)}

                      <div className={`msg-bubble ${deleted ? "msg-deleted" : ""} ${isStickerMessage ? "msg-bubble--sticker" : ""} ${contentCard ? "msg-bubble--card" : ""}`}>
                        {!mine && isGroupDialog(activeDialog) && firstInGroup ? (
                          <div className="msg-sender">{senderName}</div>
                        ) : null}

                        {renderReplyBlock(message)}

                        {deleted ? (
                          <div className="msg-text">Сообщение удалено</div>
                        ) : isStickerMessage ? (
                          <div className="msg-sticker" title="Стикер">{message.sticker.emoji}</div>
                        ) : contentCard ? (
                          renderContentCard(contentCard)
                        ) : (
                          <div className="msg-text">{messageText}</div>
                        )}

                        {!deleted ? renderMessageAttachments(message) : null}

                        {!deleted && verificationAction ? (
                          <div className="chat-action-inline">
                            <div className="chat-action-inline__title">Решение по заявке психолога</div>
                            {verificationUserId ? (
                              <button
                                type="button"
                                className="chat-action-inline__profile-link"
                                onClick={(event) => openPsychologistProfile(event, { userId: verificationUserId })}
                              >
                                Открыть профиль психолога
                              </button>
                            ) : null}
                            {!verificationResolved ? (
                              <div className="chat-action-inline__actions">
                                <button
                                  type="button"
                                  className="chat-action-inline__btn chat-action-inline__btn--approve"
                                  onClick={() => onVerificationAction(message, "approve")}
                                  disabled={verificationPending}
                                >
                                  Верифицировать
                                </button>
                                <button
                                  type="button"
                                  className="chat-action-inline__btn chat-action-inline__btn--reject"
                                  onClick={() => onVerificationAction(message, "reject")}
                                  disabled={verificationPending}
                                >
                                  Отклонить
                                </button>
                              </div>
                            ) : null}
                            {verificationResolved ? (
                              <div className={`chat-action-inline__state chat-action-inline__state--${verificationResolved}`}>
                                Заключение: {verificationResolved === "approve" ? "Одобрено" : "Отклонено"}
                                {verificationReviewerName ? ` • Админ: ${verificationReviewerName}` : ""}
                              </div>
                            ) : null}
                            {verificationActionState?.error ? (
                              <div className="chat-action-inline__state chat-action-inline__state--error">
                                {verificationActionState.error}
                              </div>
                            ) : null}
                          </div>
                        ) : null}

                        {!deleted && reactions.length ? (
                          <div className="msg-reactions">
                            {reactions.map((reaction) => (
                              <button
                                type="button"
                                key={`${row.key}-${reaction.emoji}`}
                                className={`msg-reaction ${reaction.me ? "own" : ""}`}
                                data-reaction-emoji={reaction.emoji}
                                onClick={(event) => onToggleReaction(message, reaction.emoji, !reaction.me, event)}
                                onContextMenu={(event) => onReactionUsersClick(message, reaction, event)}
                                title={reaction.me ? "Убрать реакцию. Правый клик — кто поставил" : "Поставить реакцию. Правый клик — кто поставил"}
                              >
                                {reaction.emoji}
                                <span className="msg-reaction-count">{reaction.count}</span>
                              </button>
                            ))}
                          </div>
                        ) : null}

                        {!deleted && String(reactionUsersState.messageId ?? "") === String(message?.id ?? "") ? (
                          <div
                            className="chat-reaction-users-popover"
                            role="dialog"
                            aria-label="Кто поставил реакцию"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="chat-reaction-users-popover__head">
                              <span>{reactionUsersState.emoji}</span>
                              <strong>Реакция</strong>
                              <button type="button" onClick={closeReactionUsers} aria-label="Закрыть">×</button>
                            </div>
                            {reactionUsersState.loading ? (
                              <div className="chat-reaction-users-popover__state">Загружаем...</div>
                            ) : reactionUsersState.error ? (
                              <div className="chat-reaction-users-popover__state is-error">{reactionUsersState.error}</div>
                            ) : reactionUsersState.users.length ? (
                              <div className="chat-reaction-users-popover__list">
                                {reactionUsersState.users.map((user, index) => (
                                  <div className="chat-reaction-users-popover__item" key={`${user.userId || "user"}-${index}`}>
                                    <div className="chat-reaction-users-popover__avatar">{initials(user.name || "U")}</div>
                                    <div className="chat-reaction-users-popover__name">{user.name}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="chat-reaction-users-popover__state">Backend пока не отдал список пользователей</div>
                            )}
                          </div>
                        ) : null}

                        {renderMessageMeta(message, mine)}

                        {!deleted && failed ? (
                          <div className="msg-error">
                            <span className="msg-error-text">Не отправлено</span>
                            <button type="button" className="msg-error-retry" onClick={() => onRetryFailed(message)}>
                              Повторить
                            </button>
                          </div>
                        ) : null}

                        {/* Reaction picker is rendered globally below, outside message bubbles. */}
                      </div>

                      {!deleted ? (
                        <div className="msg-actions">
                          <button
                            type="button"
                            className="msg-action-btn"
                            title="Ответить"
                            onClick={() => {
                              setMobileActionsMessageKey("");
                              setReplyTo({
                                id: message?.id || null,
                                messageId: message?.id || null,
                                clientId: message?.clientId || null,
                                key: message?.id || message?.clientId || null,
                                fromUserId: message?.fromUserId || null,
                                fromName: senderName,
                                text: String(message?.text || "").trim() || "Вложение",
                                createdAt: message?.createdAt || null,
                              });
                            }}
                          >
                            <Icon size={12}>
                              <polyline points="9 14 4 9 9 4" />
                              <path d="M20 20v-7a4 4 0 00-4-4H4" />
                            </Icon>
                          </button>

                          <button
                            type="button"
                            className="msg-action-btn"
                            title="Реакция"
                            onClick={(event) => showReactionPicker(message, event)}
                          >
                            <Icon size={12}>
                              <circle cx="12" cy="12" r="10" />
                              <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                              <line x1="9" y1="9" x2="9.01" y2="9" />
                              <line x1="15" y1="9" x2="15.01" y2="9" />
                            </Icon>
                          </button>

                          {isServerMessageId(message?.id) ? (
                            <button
                              type="button"
                              className="msg-action-btn"
                              title="Закрепить для себя"
                              disabled={pinSavingKey === `${message.id}:self`}
                              onClick={() => {
                                setMobileActionsMessageKey("");
                                pinMessage(message, "self");
                              }}
                            >
                              <Icon size={13}>
                                <path d="M14 3l7 7" />
                                <path d="M5 21l6.5-6.5" />
                                <path d="M6.5 6.5l11 11" />
                                <path d="M8 5l11 11-4 1-8-8 1-4z" />
                              </Icon>
                            </button>
                          ) : null}

                          {canManageChat && isServerMessageId(message?.id) ? (
                            <button
                              type="button"
                              className="msg-action-btn msg-action-btn--pin-global"
                              title="Закрепить для всех"
                              disabled={pinSavingKey === `${message.id}:global`}
                              onClick={() => {
                                setMobileActionsMessageKey("");
                                pinMessage(message, "global");
                              }}
                            >
                              <Icon size={13}>
                                <path d="M12 21s7-4.5 7-11a7 7 0 10-14 0c0 6.5 7 11 7 11z" />
                                <circle cx="12" cy="10" r="2.5" />
                              </Icon>
                            </button>
                          ) : null}

                          {mine && isServerMessageId(message?.id) ? (
                            <button
                              type="button"
                              className="msg-action-btn"
                              title="Редактировать"
                              onClick={() => {
                                setMobileActionsMessageKey("");
                                startEditMessage(message);
                              }}
                            >
                              <Icon size={12}>
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                              </Icon>
                            </button>
                          ) : null}

                          {mine && isServerMessageId(message?.id) ? (
                            <button
                              type="button"
                              className="msg-action-btn"
                              title="Удалить"
                              onClick={() => {
                                setMobileActionsMessageKey("");
                                onDeleteMessage(message);
                              }}
                            >
                              <Icon size={12}>
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                              </Icon>
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}

                {!messagesLoading && orderedMessages.length === 0 ? (
                  <div className="system-msg">
                    <span>Сообщений пока нет</span>
                  </div>
                ) : null}
              </div>

              <ChatScrollFab visible={showScrollBottomFab} onClick={() => scrollToBottom(true)} />

              {typingText && !activeDialog?.locked ? (
                <div className="typing-indicator">
                  <div className="typing-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                  {typingText}
                </div>
              ) : null}

              {!chatCanWrite && !editingMessage ? (
                <div className={`chat-locked ${chatMuteNotice ? "chat-locked--muted" : ""}`}>
                  <div className="chat-locked__icon" aria-hidden="true">
                    <Icon size={16}>
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </Icon>
                  </div>
                  <div className="chat-locked__copy">
                    <div className="chat-locked__title">
                      {chatMuteNotice ? "Ввод сообщений временно запрещен" : "Отправка сообщений ограничена"}
                    </div>
                    <div className="chat-locked__text">
                      {chatWriteRestrictionText || "Отправка сообщений ограничена"}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="chat-input-area">
                  {replyTo ? (
                    <div className="input-reply-preview">
                      <div className="input-reply-content">
                        <div className="input-reply-name">{replyTo.fromName || "Сообщение"}</div>
                        <div className="input-reply-text">{replyTo.text || "Вложение"}</div>
                      </div>
                      <button type="button" className="input-reply-close" onClick={() => setReplyTo(null)}>
                        <Icon size={14}>
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </Icon>
                      </button>
                    </div>
                  ) : null}

                  {(editingExistingAttachments.length || pendingFiles.length) ? (
                    <div className="input-files">
                      {editingExistingAttachments.map((attachment) => {
                        const key = mediaAttachmentKey(attachment) || attachment?.name || attachment?.url;
                        const kind = isImageLikeFileValue(attachment) ? "image" : (attachment?.kind || "file");
                        const preview = kind === "image" ? safeExternalUrl(attachment?.url, { allowDataImage: true }) : "";
                        return (
                          <div className="input-file-chip input-file-chip--existing" key={`existing-${key}`}>
                            {preview ? (
                              <img src={preview} alt={attachment.name || "Изображение"} className="input-file-preview-img" />
                            ) : (
                              <span className="input-file-icon">📎</span>
                            )}
                            <span className="input-file-name">{attachment.name || "Вложение"}</span>
                            <button type="button" onClick={() => removeEditingExistingAttachment(attachment)} title="Убрать из сообщения">
                              ×
                            </button>
                          </div>
                        );
                      })}
                      {pendingFiles.map((file, index) => {
                        const fp = fileFingerprint(file);
                        const isImage = isImageLikeFileValue(file);
                        const preview = isImage ? pendingFilePreviews[fp] : null;
                        return (
                          <div className="input-file-chip" key={`${file.name}-${index}`}>
                            {preview ? (
                              <img src={preview} alt={file.name} className="input-file-preview-img" />
                            ) : (
                              <span className="input-file-icon">📎</span>
                            )}
                            <span className="input-file-name">{file.name}</span>
                            <button type="button" onClick={() => onRemovePendingFile(index)}>
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}

                  {editingMessage ? (
                    <div className="edit-indicator">
                      <span className="edit-indicator__label">Редактирование</span>
                      <span className="edit-indicator__preview">{editingMessage.text?.slice(0, 50)}{editingMessage.text?.length > 50 ? "..." : ""}{editingExistingAttachments.length ? ` · ${editingExistingAttachments.length} медиа` : ""}</span>
                      <button type="button" className="edit-indicator__cancel" onClick={cancelEditMessage} title="Отменить">
                        <Icon size={14}>
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </Icon>
                      </button>
                    </div>
                  ) : null}

                  <div className="input-row">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      hidden
                      accept="image/*,.pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.csv,.json"
                      onChange={onPickFiles}
                    />
                    {isPsychologistLike ? (
                      <button
                        className={`input-recommend-btn ${recommendPickerOpen ? "active" : ""}`}
                        type="button"
                        title="Предложить литературу, подкаст или тест"
                        onClick={() => {
                          setRecommendPickerOpen((prev) => !prev);
                          setEmojiPickerOpen(false);
                        }}
                      >
                        <span>＋</span>
                      </button>
                    ) : null}
                    <button className="input-attach-btn" type="button" title={chatMediaRestrictionText || "Прикрепить"} onClick={onAttachFiles} disabled={!chatCanSendMedia}>
                      <Icon size={16}>
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                      </Icon>
                    </button>

                    <div className={`input-field-wrap${editingMessage ? " is-editing" : ""}`}>
                      <textarea
                        ref={textareaRef}
                        className="input-field"
                        value={draft}
                        rows={1}
                        onChange={onDraftChange}
                        onKeyDown={onDraftKeyDown}
                        placeholder={composerPlaceholder}
                        disabled={!chatCanWrite && !editingMessage}
                      />
                      <div className="emoji-picker-container">
                        <button
                          className="input-emoji-btn"
                          type="button"
                          title="Эмодзи и стикеры"
                          onClick={toggleEmojiPicker}
                        >
                          <Icon size={16}>
                            <circle cx="12" cy="12" r="10" />
                            <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                            <line x1="9" y1="9" x2="9.01" y2="9" />
                            <line x1="15" y1="9" x2="15.01" y2="9" />
                          </Icon>
                        </button>
                        {emojiPickerOpen && (
                          <>
                            <div
                              className="emoji-picker-backdrop"
                              aria-hidden="true"
                              onClick={() => setEmojiPickerOpen(false)}
                            />
                            <div className="emoji-picker visible" onClick={(e) => e.stopPropagation()}>
                            <div className="emoji-picker-header">
                              <span>Эмодзи и стикеры</span>
                              <button
                                type="button"
                                className="emoji-picker-close"
                                onClick={() => setEmojiPickerOpen(false)}
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <line x1="18" y1="6" x2="6" y2="18" />
                                  <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                              </button>
                            </div>
                            <div className="chat-rich-picker__mode-tabs">
                              <button
                                type="button"
                                className={`chat-rich-picker__mode-btn ${pickerMode === "emoji" ? "active" : ""}`}
                                onClick={() => setPickerMode("emoji")}
                              >
                                Эмодзи
                              </button>
                              <button
                                type="button"
                                className={`chat-rich-picker__mode-btn ${pickerMode === "sticker" ? "active" : ""}`}
                                onClick={() => setPickerMode("sticker")}
                              >
                                Стикеры
                              </button>
                            </div>
                            <div className="emoji-picker-tabs">
                              {(pickerMode === "emoji" ? EMOJI_CATEGORIES : STICKER_CATEGORIES).map((cat, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  className={`emoji-picker-tab ${
                                    (pickerMode === "emoji" ? selectedEmojiCategory : selectedStickerCategory) === idx
                                      ? "active"
                                      : ""
                                  }`}
                                  title={cat.name}
                                  onClick={() => {
                                    if (pickerMode === "emoji") {
                                      setSelectedEmojiCategory(idx);
                                    } else {
                                      setSelectedStickerCategory(idx);
                                    }
                                  }}
                                >
                                  {cat.emoji[0]}
                                </button>
                              ))}
                            </div>
                            <div className="emoji-picker-grid">
                              <div
                                className={`chat-rich-picker__grid ${
                                  pickerMode === "sticker" ? "chat-rich-picker__grid--stickers" : ""
                                }`}
                              >
                                {(pickerMode === "emoji"
                                  ? EMOJI_CATEGORIES[selectedEmojiCategory]?.emoji
                                  : STICKER_CATEGORIES[selectedStickerCategory]?.emoji
                                )?.map((emoji, i) => (
                                  <button
                                    key={i}
                                    type="button"
                                    className={`emoji-picker-item ${
                                      pickerMode === "sticker" ? "chat-rich-picker__sticker-item" : ""
                                    }`}
                                    onClick={() =>
                                      pickerMode === "emoji"
                                        ? handleEmojiSelect(emoji)
                                        : handleStickerSelect(emoji)
                                    }
                                    title={
                                      pickerMode === "emoji"
                                        ? emoji
                                        : STICKER_CATEGORIES[selectedStickerCategory]?.name
                                    }
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    <button className="input-send-btn" type="button" title={editingMessage ? "Сохранить" : "Отправить"} onClick={onSend} disabled={!canSendMessage}>
                      <Icon size={16}>
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22 2 15 22 11 13 2 9 22 2" />
                      </Icon>
                    </button>
                  </div>
                  {isPsychologistLike ? (
                    <ChatRecommendationPicker
                      open={recommendPickerOpen}
                      onClose={() => setRecommendPickerOpen(false)}
                      onSend={handleSendRecommendation}
                      disabled={sending}
                    />
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>

        {activeDialog && !hideInfoPanel ? (
          <div
            className={`chat-info-panel ${isMobile && infoOpen ? "visible-mobile" : ""}`}
            style={!isMobile ? { display: infoOpen ? "flex" : "none" } : undefined}
          >
            <div className="info-header">
              <div className="info-header-title">Информация</div>
              <button className="info-close-btn" type="button" onClick={() => setInfoOpen(false)}>
                <Icon size={14}>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </Icon>
              </button>
            </div>

            <div className="info-profile">
              <div className="info-avatar" style={activeDialogAvatarUrl ? undefined : { background: "var(--mint)" }}>
                {activeDialogAvatarUrl ? <img src={safeExternalUrl(activeDialogAvatarUrl) || activeDialogAvatarUrl} alt="" /> : initials(activeDialogTitle)}
              </div>
              <div className="info-name">{activeDialogTitle}</div>
              <div className="info-role">{activeDialogIsGroup ? "Групповой чат" : "Личный диалог"}</div>
              <div className="info-status-badge">
                {activeDialogIsGroup
                  ? `${activeDialogMembers.length || activeDialog?.membersCount || 1} участников`
                  : activeDialog?.online
                  ? "В сети"
                  : "Активность неизвестна"}
              </div>
              <div className="info-quick-actions" aria-label="Действия чата">
                <button type="button" onClick={() => setMessageSearchOpen(true)}>Поиск</button>
                {canManageChat ? (
                  <button type="button" onClick={() => setChatSettingsOpen(true)}>Доступ</button>
                ) : null}
                <button type="button" onClick={toggleChatNotifications}>
                  {activeDialogNotificationsMuted ? "Вкл. звук" : "Без звука"}
                </button>
              </div>
              {activeDialogPsychologistRoute ? (
                <button
                  type="button"
                  className="info-profile-link-btn"
                  onClick={(event) => openPsychologistProfile(event, activeDialogPsychologistTarget)}
                >
                  Профиль психолога
                </button>
              ) : null}
            </div>

            <div className="info-section">
              <div className="info-section-title">Контактная информация</div>
              <div className="info-kv">
                <div><span>Участников</span><strong>{activeDialogMembers.length || 1}</strong></div>
                <div><span>Непрочитано</span><strong>{Number(activeDialog?.unreadCount || 0)}</strong></div>
                <div><span>Тип</span><strong>{activeDialogIsGroup ? "Групповой чат" : "Личный диалог"}</strong></div>
              </div>
            </div>

            {activeDialogMembers.length > 1 ? (
              <div className="info-section">
                <div className="info-section-title">Участники</div>
                {activeDialogMembers.map((member, index) => {
                  const memberId = toUserId(member?.userId ?? member?.id);
                  const memberRawUserId =
                    member?.userId ??
                    member?.id ??
                    member?.memberId ??
                    member?.member_id ??
                    member?.accountId ??
                    null;
                  const memberName = member?.name || member?.fullName || "Участник";
                  const memberHost = isMemberHost(member);
                  const memberAdmin = isMemberAdmin(member);
                  const memberProfileUrl = String(
                    member?.profileUrl ||
                      member?.psychologistProfileUrl ||
                      member?.partnerProfileUrl ||
                      member?.user?.profileUrl ||
                      member?.user?.psychologistProfileUrl ||
                      ""
                  ).trim();
                  const memberResolvedRoute =
                    resolvePsychologistProfileRoute(memberProfileUrl, memberRawUserId ?? memberId) || "";
                  const memberFallbackRoute =
                    buildPsychologistProfilePath(memberRawUserId ?? memberId) || "";
                  const memberRoute =
                    memberResolvedRoute &&
                    (isExternalHttpUrl(memberResolvedRoute) || /^\/psychologist(?:\/|$)/i.test(memberResolvedRoute))
                      ? memberResolvedRoute
                      : memberFallbackRoute;
                  const canOpenMemberPsychologistProfile = Boolean(memberRoute);
                  const isSelf = Boolean(memberId && myId && Number(memberId) === Number(myId));
                  const canRemove = canManageMembers && !isSelf && !memberHost;
                  const removeLoading = Boolean(
                    memberRemovePendingId && memberId && Number(memberRemovePendingId) === Number(memberId)
                  );
                  const memberProfileTarget = {
                    userId: memberRawUserId ?? memberId,
                    profileUrl: memberProfileUrl,
                    route: memberRoute,
                  };

                  return (
                    <div className="info-member" key={memberId || member.userId || member.id || index}>
                      {canOpenMemberPsychologistProfile ? (
                        <button
                          type="button"
                          className="info-member-profile-btn"
                          onClick={(event) => openPsychologistProfile(event, memberProfileTarget)}
                          title="Открыть профиль психолога"
                        >
                          <div className="info-member-avatar" style={(member?.avatarUrl || member?.avatar || member?.photoUrl) ? undefined : { background: "var(--lav-soft)", color: "#7B6FD4" }}>
                            {(member?.avatarUrl || member?.avatar || member?.photoUrl) ? (
                              <img src={safeExternalUrl(member.avatarUrl || member.avatar || member.photoUrl) || member.avatarUrl || member.avatar || member.photoUrl} alt="" />
                            ) : initials(memberName)}
                          </div>
                          <div className="info-member-main">
                            <div className="info-member-name info-member-name-btn">{memberName}</div>
                            {memberAdmin ? (
                              <div className={`info-member-admin-tag ${memberHost ? "is-host" : ""}`}>
                                {memberHost ? "Хост чата" : "Админ чата"}
                              </div>
                            ) : null}
                          </div>
                        </button>
                      ) : (
                        <>
                          <div className="info-member-avatar" style={(member?.avatarUrl || member?.avatar || member?.photoUrl) ? undefined : { background: "var(--lav-soft)", color: "#7B6FD4" }}>
                            {(member?.avatarUrl || member?.avatar || member?.photoUrl) ? (
                              <img src={safeExternalUrl(member.avatarUrl || member.avatar || member.photoUrl) || member.avatarUrl || member.avatar || member.photoUrl} alt="" />
                            ) : initials(memberName)}
                          </div>
                          <div className="info-member-main">
                            <div className="info-member-name">{memberName}</div>
                            {memberAdmin ? (
                              <div className={`info-member-admin-tag ${memberHost ? "is-host" : ""}`}>
                                {memberHost ? "Хост чата" : "Админ чата"}
                              </div>
                            ) : null}
                          </div>
                        </>
                      )}
                      <div className="info-member-meta">
                        <div className={`info-member-role-tag ${member?.isOnline ? "is-online" : "is-offline"}`}>
                          {member?.isOnline ? "В сети" : "Активность неизвестна"}
                        </div>
                        {canRemove ? (
                          <button
                            type="button"
                            className="info-member-remove-btn"
                            onClick={() => onRemoveMember(member)}
                            disabled={removeLoading}
                          >
                            {removeLoading ? "Удаляем..." : "Удалить"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {activeDialogIsGroup && canManageMembers ? (
              <div className="info-section">
                <div className="info-section-title">Пригласить участников</div>
                <div className="info-member-search">
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(event) => setMemberSearch(event.target.value)}
                    placeholder="Введите имя или email"
                  />
                </div>

                {memberSearch.trim().length < 2 ? (
                  <div className="info-empty">Введите минимум 2 символа для поиска</div>
                ) : null}
                {memberSearchLoading ? <div className="info-empty">Ищем пользователей...</div> : null}
                {memberSearchError ? <div className="info-inline-error">{memberSearchError}</div> : null}
                {!memberSearchLoading && !memberSearchError && memberSearch.trim().length >= 2 && memberSearchResults.length === 0 ? (
                  <div className="info-empty">Подходящие пользователи не найдены</div>
                ) : null}

                {memberSearchResults.length > 0 ? (
                  <div className="info-member-search-list">
                    {memberSearchResults.map((user) => {
                      const inviteLoading = Boolean(
                        memberInvitePendingId && Number(memberInvitePendingId) === Number(user.id)
                      );
                      return (
                        <div className="info-member-search-item" key={user.id}>
                          <div className="info-member-avatar" style={user.avatarUrl ? undefined : { background: "var(--mint-soft)", color: "#1b9a63" }}>
                            {user.avatarUrl ? <img src={safeExternalUrl(user.avatarUrl) || user.avatarUrl} alt="" /> : initials(user.name || "U")}
                          </div>
                          <div className="info-member-search-name">{user.name}</div>
                          <button
                            type="button"
                            className="info-member-invite-btn"
                            disabled={inviteLoading}
                            onClick={() => onInviteMember(user)}
                          >
                            {inviteLoading ? "Добавляем..." : "Пригласить"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="info-section">
              <div className="info-section-title">Общие файлы</div>
              {sharedFiles.length === 0 ? <div className="info-empty">Файлов пока нет</div> : null}

              {sharedFiles.slice(0, 12).map((file) => {
                const safeLink = safeExternalUrl(file?.url);
                return (
                  <button
                    type="button"
                    className="info-file-item"
                    key={file.id || safeLink || file.name}
                    onClick={() => openMediaAtAttachment(file)}
                  >
                    <div className="info-file-icon">
                      <Icon size={14}>
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </Icon>
                    </div>
                    <div>
                      <div className="info-file-name">{file.name || "Файл"}</div>
                      <div className="info-file-meta">{bytesLabel(file.size)} {file.createdAt ? `· ${formatDialogTime(file.createdAt)}` : ""}</div>
                    </div>
                  </button>
                );
              })}
              {imageFiles.length > 0 && (
                <button
                  type="button"
                  className="info-gallery-btn"
                  onClick={() => setMediaGallery({ images: imageFiles, index: 0 })}
                >
                  📷 Смотреть медиа ({imageFiles.length})
                </button>
              )}
            </div>


          </div>
        ) : null}
      </div>


      {!embedded ? (
        <div className="mobile-bottom-nav">
        <div className="mobile-nav-item">
          <Icon>
            <circle cx="9" cy="7" r="4" />
            <path d="M2 21v-2a4 4 0 014-4h6a4 4 0 014 4v2" />
          </Icon>
          <span>Клиенты</span>
        </div>
        <div className="mobile-nav-item">
          <Icon>
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
          </Icon>
          <span>Календарь</span>
        </div>
        <div className="mobile-nav-item active">
          <Icon>
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
          </Icon>
          <span>Чаты</span>
        </div>
        <div className="mobile-nav-item">
          <Icon>
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M8 15v-4M12 15v-6M16 15v-2" />
          </Icon>
          <span>Ещё</span>
        </div>
        </div>
      ) : null}

      {canManageChat && chatSettingsOpen && !hideDialogSettings ? (
        <div
          className="chat-settings-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Настройки чата"
          onMouseDown={(event) => {
            if (event.target !== event.currentTarget) return;
            setChatSettingsOpen(false);
          }}
        >
          <div
            className="chat-settings-modal__dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="chat-settings-modal__head">
              <div>
                <div className="chat-settings-modal__eyebrow">Управление чатом</div>
                <div className="chat-settings-modal__title">Настройки доступа</div>
                <div className="chat-settings-modal__subtitle">Переименование, права на сообщения и медиа.</div>
              </div>
              <button type="button" className="chat-settings-modal__close" onClick={() => setChatSettingsOpen(false)} aria-label="Закрыть">×</button>
            </div>

            <div className="chat-settings-modal__body">
              <div className="chat-settings-avatar">
                <div className="chat-settings-avatar__preview" style={(chatSettingsDraft.avatarPreview || activeDialogAvatarUrl) ? undefined : { background: "var(--mint)" }}>
                  {(chatSettingsDraft.avatarPreview || activeDialogAvatarUrl) ? (
                    <img src={chatSettingsDraft.avatarPreview || safeExternalUrl(activeDialogAvatarUrl) || activeDialogAvatarUrl} alt="" />
                  ) : (
                    initials(activeDialogTitle)
                  )}
                </div>
                <label className="chat-settings-avatar__button">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      if (!file) return;
                      setChatSettingsDraft((prev) => {
                        if (prev.avatarPreview && prev.avatarPreview.startsWith("blob:")) {
                          URL.revokeObjectURL(prev.avatarPreview);
                        }
                        return { ...prev, avatarFile: file, avatarPreview: URL.createObjectURL(file) };
                      });
                    }}
                  />
                  Сменить аватар
                </label>
              </div>

              <label className="chat-settings-field">
                <span>Название чата</span>
                <input
                  type="text"
                  value={chatSettingsDraft.title}
                  onChange={(event) => setChatSettingsDraft((prev) => ({ ...prev, title: event.target.value }))}
                  placeholder="Название чата"
                />
              </label>

              <label className="chat-settings-field">
                <span>Кто может писать</span>
                <select
                  value={chatSettingsDraft.writeMode}
                  onChange={(event) => setChatSettingsDraft((prev) => ({ ...prev, writeMode: event.target.value }))}
                >
                  <option value={CHAT_WRITE_MODE_ALL}>Все участники</option>
                  <option value={CHAT_WRITE_MODE_HOST_ONLY}>Только хост / админы</option>
                  <option value={CHAT_WRITE_MODE_SELECTED}>Только выбранные</option>
                </select>
              </label>

              {chatSettingsDraft.writeMode === CHAT_WRITE_MODE_SELECTED ? (
                <div className="chat-settings-members">
                  {activeDialogMembers.map((member) => {
                    const memberId = toUserId(member.userId ?? member.id);
                    if (!memberId) return null;
                    const checked = chatSettingsDraft.allowedUserIds.some((id) => Number(id) === Number(memberId));
                    return (
                      <label className="chat-settings-member" key={memberId}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggleAllowedWriter(memberId)}
                        />
                        <span>{member.name || member.fullName || `Участник ${memberId}`}</span>
                      </label>
                    );
                  })}
                </div>
              ) : null}

              <label className="chat-settings-field">
                <span>Отправка медиа</span>
                <select
                  value={chatSettingsDraft.mediaMode}
                  onChange={(event) => setChatSettingsDraft((prev) => ({ ...prev, mediaMode: event.target.value }))}
                >
                  <option value={CHAT_MEDIA_MODE_ALL}>Разрешена всем, кто может писать</option>
                  <option value={CHAT_MEDIA_MODE_HOST_ONLY}>Только хост / админы</option>
                  <option value={CHAT_MEDIA_MODE_DISABLED}>Отключена</option>
                </select>
              </label>

              <div className="chat-settings-modal__note">
                Если backend ещё не поддерживает настройки, фронт применит их локально до перезагрузки страницы.
              </div>
            </div>

            <div className="chat-settings-modal__actions">
              <button type="button" onClick={() => setChatSettingsOpen(false)}>Отмена</button>
              <button type="button" className="primary" disabled={chatSettingsSaving} onClick={handleSaveChatSettings}>
                {chatSettingsSaving ? "Сохраняем..." : "Сохранить"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {reactionPickerMessage ? (
        <ReactionPicker
          message={reactionPickerMessage.message || reactionPickerMessage}
          anchor={reactionPickerMessage}
          onSelect={onReactionSelect}
          onClose={() => setReactionPickerMessage(null)}
        />
      ) : null}

      <ChatConfirmDelete
        message={confirmDeleteMessage}
        onCancel={() => setConfirmDeleteMessage(null)}
        onConfirm={onConfirmDelete}
      />

      {mediaGallery ? (
        <ChatMediaGallery
          images={mediaGallery.images}
          index={mediaGallery.index}
          onClose={() => setMediaGallery(null)}
          onPrev={() =>
            setMediaGallery((prev) =>
              prev ? { ...prev, index: Math.max(0, prev.index - 1) } : null
            )
          }
          onNext={() =>
            setMediaGallery((prev) =>
              prev
                ? {
                    ...prev,
                    index: Math.min(prev.images.length - 1, prev.index + 1),
                  }
                : null
            )
          }
          onSelect={(index) =>
            setMediaGallery((prev) =>
              prev
                ? {
                    ...prev,
                    index: Math.min(Math.max(0, Number(index) || 0), Math.max(0, prev.images.length - 1)),
                  }
                : null
            )
          }
          onJumpToMessage={(messageKey) => {
            setMediaGallery(null);
            window.setTimeout(() => jumpToMessage(messageKey), 80);
          }}
        />
      ) : null}
    </div>
  );
}
